import type { Poem } from '../types'

/**
 * Works added to extend the map beyond the school curriculum. Each entry is
 * dated and placed individually: the date evidence explains the chronology,
 * the place evidence explains why this coordinate (and not just the author's
 * city) is used, and existing place IDs are reused when the site is the same
 * so that works from different centuries meet on the map.
 */

const sourceUrl = 'https://github.com/chinese-poetry/chinese-poetry'

type Chronology = Pick<Poem, 'year' | 'yearLabel' | 'eraLabel' | 'datePrecision' | 'dateEvidence'>
type Anchor = Pick<Poem, 'placeId' | 'placeName' | 'longitude' | 'latitude'>

const when = (
  year: number,
  yearLabel: string,
  eraLabel: string,
  datePrecision: Poem['datePrecision'],
  dateEvidence: string,
): Chronology => ({ year, yearLabel, eraLabel, datePrecision, dateEvidence })

const at = (placeId: string, placeName: string, longitude: number, latitude: number): Anchor => ({
  placeId,
  placeName,
  longitude,
  latitude,
})

export const expandedPoems: Poem[] = [
  // 先秦
  {
    id: 'shijing-wu-yi', title: '秦风·无衣', author: '佚名', dynasty: 'pre-qin',
    lines: [
      '岂曰无衣？与子同袍。王于兴师，修我戈矛。与子同仇！',
      '岂曰无衣？与子同泽。王于兴师，修我矛戟。与子偕作！',
      '岂曰无衣？与子同裳。王于兴师，修我甲兵。与子偕行！',
    ],
    ...when(-506, '传为前506年', '春秋 · 秦哀公出师救楚', 'disputed', '《左传·定公四年》记申包胥乞师，秦哀公为之赋《无衣》；也有学者认为诗早于此事，只是被赋诵。'),
    ...at('qin-yongcheng', '秦都雍城 · 雍水河岸', 107.15, 34.44),
    relation: 'associated', confidence: 'low',
    evidence: '《无衣》属《秦风》，以秦都雍城作为秦地军歌的传承关联地，诗中未写具体地点。',
    sourceLabel: '《诗经·秦风》', sourceUrl,
    accent: '#c47f5d', visualEffect: 'sun-river', visualEffectLabel: '同袍 · 戈矛',
  },
  {
    id: 'shijing-shuo-shu', title: '魏风·硕鼠', author: '佚名', dynasty: 'pre-qin',
    lines: [
      '硕鼠硕鼠，无食我黍！三岁贯女，莫我肯顾。逝将去女，适彼乐土。乐土乐土，爰得我所。',
      '硕鼠硕鼠，无食我麦！三岁贯女，莫我肯德。逝将去女，适彼乐国。乐国乐国，爰得我直。',
      '硕鼠硕鼠，无食我苗！三岁贯女，莫我肯劳。逝将去女，适彼乐郊。乐郊乐郊，谁之永号？',
    ],
    ...when(-661, '前661年以前', '春秋 · 魏国被晋所灭之前', 'period', '《魏风》所属的姬姓魏国于前661年为晋献公所灭，诗歌只能约定在此前魏地流传的时期。'),
    ...at('wei-ruicheng', '魏国故地 · 芮城河曲', 110.69, 34.69),
    relation: 'associated', confidence: 'low',
    evidence: '春秋魏国在今山西芮城一带，以魏国故城作为《魏风》民歌的采集关联地。',
    sourceLabel: '《诗经·魏风》', sourceUrl,
    accent: '#b99a62', visualEffect: 'morning-rain', visualEffectLabel: '黍麦 · 乐土',
  },
  {
    id: 'shijing-mu-gua', title: '卫风·木瓜', author: '佚名', dynasty: 'pre-qin',
    lines: [
      '投我以木瓜，报之以琼琚。匪报也，永以为好也！',
      '投我以木桃，报之以琼瑶。匪报也，永以为好也！',
      '投我以木李，报之以琼玖。匪报也，永以为好也！',
    ],
    ...when(-650, '约前7世纪', '春秋 · 卫国歌谣', 'period', '《毛诗序》系于卫文公复国时齐桓公救卫之事，后世多视为民间赠答歌谣，只能约定在春秋卫国时期。'),
    ...at('chaoge', '卫都朝歌 · 淇县故城', 114.2, 35.62),
    relation: 'associated', confidence: 'low',
    evidence: '《卫风》采自卫国，以卫都朝歌故城作为民歌关联地，诗中的赠答没有具体场所。',
    sourceLabel: '《诗经·卫风》', sourceUrl,
    accent: '#c8a45c', visualEffect: 'petals-embers', visualEffectLabel: '木瓜 · 琼琚',
  },
  {
    id: 'qu-yuan-ju-song', title: '九章·橘颂', author: '屈原', dynasty: 'pre-qin',
    lines: [
      '后皇嘉树，橘徕服兮。受命不迁，生南国兮。',
      '深固难徙，更壹志兮。绿叶素荣，纷其可喜兮。',
      '曾枝剡棘，圆果抟兮。青黄杂糅，文章烂兮。',
      '精色内白，类任道兮。纷缊宜修，姱而不丑兮。',
      '嗟尔幼志，有以异兮。独立不迁，岂不可喜兮？',
      '深固难徙，廓其无求兮。苏世独立，横而不流兮。',
      '闭心自慎，终不失过兮。秉德无私，参天地兮。',
      '愿岁并谢，与长友兮。淑离不淫，梗其有理兮。',
      '年岁虽少，可师长兮。行比伯夷，置以为像兮。',
    ],
    ...when(-320, '约前320年', '楚怀王前期 · 屈原早年', 'disputed', '王逸以为放逐后作，现代多数学者据“年岁虽少”“嗟尔幼志”判为屈原青年时期作品，年代仍有异说。'),
    ...at('zigui-leping', '秭归 · 乐平里', 110.98, 30.83),
    relation: 'associated', confidence: 'low',
    evidence: '秭归是屈原传统故里，峡江两岸至今产橘；诗以南国之橘自况，故以故里作关联地。',
    sourceLabel: '《楚辞·九章》', sourceUrl,
    accent: '#d59a4a', visualEffect: 'sun-river', visualEffectLabel: '南国 · 嘉树',
  },
  {
    id: 'qu-yuan-guo-shang', title: '九歌·国殇', author: '屈原', dynasty: 'pre-qin',
    lines: [
      '操吴戈兮被犀甲，车错毂兮短兵接。旌蔽日兮敌若云，矢交坠兮士争先。',
      '凌余阵兮躐余行，左骖殪兮右刃伤。霾两轮兮絷四马，援玉枹兮击鸣鼓。',
      '天时怼兮威灵怒，严杀尽兮弃原野。出不入兮往不反，平原忽兮路超远。',
      '带长剑兮挟秦弓，首身离兮心不惩。诚既勇兮又以武，终刚强兮不可凌。',
      '身既死兮神以灵，魂魄毅兮为鬼雄。',
    ],
    ...when(-300, '约前312—前299年', '楚怀王后期 · 丹阳蓝田战后', 'range', '《国殇》祭祀为国战死者，学者多联系前312年丹阳、蓝田之败，但《九歌》整体写作年代仍有不同意见。'),
    ...at('ying-jinancheng', '郢都 · 纪南城', 112.18, 30.43),
    relation: 'associated', confidence: 'low',
    evidence: '《九歌》是楚国祭歌，以楚都郢的纪南城遗址作为祭祀传统的关联地，并非战场位置。',
    sourceLabel: '《楚辞·九歌》', sourceUrl,
    accent: '#b5563f', visualEffect: 'petals-embers', visualEffectLabel: '吴戈 · 鬼雄',
  },

  // 两汉
  {
    id: 'xiang-yu-gaixia', title: '垓下歌', author: '项羽', dynasty: 'han',
    lines: [
      '力拔山兮气盖世。时不利兮骓不逝。',
      '骓不逝兮可奈何！虞兮虞兮奈若何！',
    ],
    ...when(-202, '前202年', '汉五年 · 垓下之围', 'exact', '《史记·项羽本纪》记项羽被围垓下，夜闻四面楚歌，悲歌慷慨，自为诗曰，时在汉五年十二月。'),
    ...at('gaixia', '垓下 · 楚军围营', 117.63, 33.35),
    relation: 'composed_at', confidence: 'medium',
    evidence: '垓下具体位置有灵璧、鹿邑等说，这里采用灵璧东南的通行说法，坐标只到古战场范围。',
    sourceLabel: '《史记·项羽本纪》', sourceUrl,
    accent: '#a84d3c', visualEffect: 'petals-embers', visualEffectLabel: '楚歌 · 乌骓',
  },
  {
    id: 'liu-che-qiu-feng', title: '秋风辞', author: '刘彻', dynasty: 'han',
    lines: [
      '秋风起兮白云飞，草木黄落兮雁南归。',
      '兰有秀兮菊有芳，怀佳人兮不能忘。',
      '泛楼船兮济汾河，横中流兮扬素波。',
      '箫鼓鸣兮发棹歌，欢乐极兮哀情多。',
      '少壮几时兮奈老何！',
    ],
    ...when(-113, '前113年秋', '汉武帝元鼎四年 · 汾阴祠后土', 'circa', '《文选》序称武帝行幸河东祠后土，顾视帝京欣然，中流与群臣饮宴而作；汾阴祠后土在元鼎四年。'),
    ...at('fenyin-houtu', '河东汾阴 · 后土祠汾河渡', 110.55, 35.43),
    relation: 'composed_at', confidence: 'medium',
    evidence: '辞序说泛楼船济汾河，汾阴后土祠在今万荣县汾河入黄河处，是可考的创作场景。',
    sourceLabel: '《文选》卷四十五', sourceUrl,
    accent: '#c9a36a', visualEffect: 'river-flight', visualEffectLabel: '楼船 · 秋风',
  },
  {
    id: 'gushi-xingxing', title: '古诗十九首·行行重行行', author: '佚名', dynasty: 'han',
    lines: [
      '行行重行行，与君生别离。相去万余里，各在天一涯。',
      '道路阻且长，会面安可知？胡马依北风，越鸟巢南枝。',
      '相去日已远，衣带日已缓。浮云蔽白日，游子不顾反。',
      '思君令人老，岁月忽已晚。弃捐勿复道，努力加餐饭。',
    ],
    ...when(160, '约2世纪中后期', '东汉桓灵年间 · 文人五言', 'period', '《古诗十九首》作者无考，学界多据五言成熟程度与内容定在东汉后期，无法落到某一年。'),
    ...at('luoyang-taixue', '东汉雒阳城 · 太学文人圈', 112.6, 34.73),
    relation: 'associated', confidence: 'low',
    evidence: '十九首多写游宦京洛的士人离思，以东汉雒阳太学作为作者群体的关联地而非别离现场。',
    sourceLabel: '《文选》卷二十九', sourceUrl,
    accent: '#9aa8c4', visualEffect: 'cloud-crane', visualEffectLabel: '胡马 · 越鸟',
  },
  {
    id: 'cao-cao-duan-ge-xing', title: '短歌行', author: '曹操', dynasty: 'han',
    lines: [
      '对酒当歌，人生几何！譬如朝露，去日苦多。',
      '慨当以慷，忧思难忘。何以解忧？唯有杜康。',
      '青青子衿，悠悠我心。但为君故，沉吟至今。',
      '呦呦鹿鸣，食野之苹。我有嘉宾，鼓瑟吹笙。',
      '明明如月，何时可掇？忧从中来，不可断绝。',
      '越陌度阡，枉用相存。契阔谈讌，心念旧恩。',
      '月明星稀，乌鹊南飞。绕树三匝，何枝可依？',
      '山不厌高，海不厌深。周公吐哺，天下归心。',
    ],
    ...when(210, '约208—210年', '建安年间 · 求贤之际', 'disputed', '《三国演义》置于赤壁横槊赋诗并不可信；诗意与建安十五年《求贤令》相近，学者多系于此前后。'),
    ...at('ye-tongque', '邺城 · 铜雀台', 114.36, 36.33),
    relation: 'associated', confidence: 'low',
    evidence: '曹操建安中后期以邺城为根据地并在此宴集文士，诗中宴饮求贤的场景与邺下相关联。',
    sourceLabel: '《乐府诗集·相和歌辞》', sourceUrl,
    accent: '#c6a15e', visualEffect: 'moon-fire', visualEffectLabel: '月明 · 乌鹊',
  },
  {
    id: 'cao-zhi-bai-ma', title: '白马篇', author: '曹植', dynasty: 'han',
    lines: [
      '白马饰金羁，连翩西北驰。借问谁家子，幽并游侠儿。',
      '少小去乡邑，扬声沙漠垂。宿昔秉良弓，楛矢何参差。',
      '控弦破左的，右发摧月支。仰手接飞猱，俯身散马蹄。',
      '狡捷过猴猿，勇剽若豹螭。边城多警急，虏骑数迁移。',
      '羽檄从北来，厉马登高堤。长驱蹈匈奴，左顾凌鲜卑。',
      '弃身锋刃端，性命安可怀？父母且不顾，何言子与妻！',
      '名编壮士籍，不得中顾私。捐躯赴国难，视死忽如归。',
    ],
    ...when(215, '约211—220年', '建安后期 · 曹植邺下时期', 'range', '诗风昂扬，一般归入曹植前期即建安年间在邺城的作品，具体年份没有题序可考。'),
    ...at('ye-tongque', '邺城 · 铜雀台', 114.36, 36.33),
    relation: 'associated', confidence: 'low',
    evidence: '白马篇写幽并游侠而非作者行迹，以曹植建安年间居住的邺城作为作品关联地。',
    sourceLabel: '《曹子建集》', sourceUrl,
    accent: '#d7c6a0', visualEffect: 'sun-river', visualEffectLabel: '白马 · 羽檄',
  },

  // 魏晋
  {
    id: 'ruan-ji-yong-huai-1', title: '咏怀八十二首·其一', author: '阮籍', dynasty: 'wei-jin',
    lines: [
      '夜中不能寐，起坐弹鸣琴。薄帷鉴明月，清风吹我襟。',
      '孤鸿号外野，翔鸟鸣北林。徘徊将何见？忧思独伤心。',
    ],
    ...when(255, '约249—263年', '曹魏正始以后 · 司马氏专政', 'range', '《咏怀》非一时一地之作，一般认为写于高平陵之变后司马氏专权、阮籍佯狂避祸的十余年间。'),
    ...at('weishi-ruanji', '陈留尉氏 · 阮籍故里', 114.19, 34.41),
    relation: 'associated', confidence: 'low',
    evidence: '阮籍为陈留尉氏人，诗中夜不能寐的地点未写明，以其故里作为人物关联地。',
    sourceLabel: '《阮步兵集》', sourceUrl,
    accent: '#8b9bb8', visualEffect: 'moon-fire', visualEffectLabel: '鸣琴 · 孤鸿',
  },
  {
    id: 'zuo-si-yong-shi-2', title: '咏史·其二', author: '左思', dynasty: 'wei-jin',
    lines: [
      '郁郁涧底松，离离山上苗。以彼径寸茎，荫此百尺条。',
      '世胄蹑高位，英俊沉下僚。地势使之然，由来非一朝。',
      '金张藉旧业，七叶珥汉貂。冯公岂不伟，白首不见招。',
    ],
    ...when(290, '约282—300年', '西晋太康至元康年间', 'range', '左思随妹入洛后仕途不显，任秘书郎期间有感门阀而作咏史诗，一般系于太康、元康年间。'),
    ...at('jin-luoyang-mishu', '西晋洛阳 · 秘书省', 112.62, 34.72),
    relation: 'associated', confidence: 'low',
    evidence: '左思在洛阳任秘书郎，门第压抑的体验来自西晋京城，故以洛阳秘书省作关联地。',
    sourceLabel: '《文选》卷二十一', sourceUrl,
    accent: '#7fa392', visualEffect: 'cloud-crane', visualEffectLabel: '涧松 · 山苗',
  },
  {
    id: 'tao-yuanming-guiyuan-1', title: '归园田居·其一', author: '陶渊明', dynasty: 'wei-jin',
    lines: [
      '少无适俗韵，性本爱丘山。误落尘网中，一去三十年。',
      '羁鸟恋旧林，池鱼思故渊。开荒南野际，守拙归园田。',
      '方宅十余亩，草屋八九间。榆柳荫后檐，桃李罗堂前。',
      '暧暧远人村，依依墟里烟。狗吠深巷中，鸡鸣桑树颠。',
      '户庭无尘杂，虚室有余闲。久在樊笼里，复得返自然。',
    ],
    ...when(406, '406年', '东晋义熙二年 · 归田次年', 'circa', '组诗写辞官归田后的第一个春夏，陶渊明义熙元年冬辞彭泽令，研究者多系于义熙二年。'),
    ...at('chaisang', '柴桑 · 庐山南麓', 115.88, 29.46),
    relation: 'composed_at', confidence: 'medium',
    evidence: '陶渊明归田后居柴桑故里，诗中草屋、墟里即其田居，具体村落有上京、南村诸说。',
    sourceLabel: '《陶渊明集》卷二', sourceUrl,
    accent: '#96b27f', visualEffect: 'cloud-crane', visualEffectLabel: '羁鸟 · 墟烟',
  },
  {
    id: 'tao-yuanming-za-shi-1', title: '杂诗·其一', author: '陶渊明', dynasty: 'wei-jin',
    lines: [
      '人生无根蒂，飘如陌上尘。分散逐风转，此已非常身。',
      '落地为兄弟，何必骨肉亲！得欢当作乐，斗酒聚比邻。',
      '盛年不重来，一日难再晨。及时当勉励，岁月不待人。',
    ],
    ...when(414, '约414年', '东晋义熙十年前后 · 陶渊明五十岁', 'circa', '《杂诗》第六首自言“奈何五十年”，组诗因此多系于义熙十年前后陶渊明五十岁时。'),
    ...at('chaisang-lixiang', '柴桑 · 栗里', 115.92, 29.49),
    relation: 'associated', confidence: 'low',
    evidence: '陶渊明晚年曾移居栗里，与邻里斗酒往还；诗中比邻相聚的生活以栗里作关联地。',
    sourceLabel: '《陶渊明集》卷四', sourceUrl,
    accent: '#b4a06a', visualEffect: 'morning-rain', visualEffectLabel: '陌尘 · 斗酒',
  },

  // 南北朝
  {
    id: 'xie-lingyun-chishang-lou', title: '登池上楼', author: '谢灵运', dynasty: 'southern-northern',
    lines: [
      '潜虬媚幽姿，飞鸿响远音。薄霄愧云浮，栖川怍渊沉。',
      '进德智所拙，退耕力不任。徇禄反穷海，卧疴对空林。',
      '衾枕昧节候，褰开暂窥临。倾耳聆波澜，举目眺岖嵚。',
      '初景革绪风，新阳改故阴。池塘生春草，园柳变鸣禽。',
      '祁祁伤豳歌，萋萋感楚吟。索居易永久，离群难处心。',
      '持操岂独古，无闷征在今。',
    ],
    ...when(423, '423年初春', '宋景平元年 · 永嘉太守任上', 'circa', '谢灵运永初三年秋到永嘉，卧病一冬，诗写病起登楼见初春景物，因此系于次年即景平元年春。'),
    ...at('yongjia-chishang', '永嘉郡 · 池上楼', 120.65, 28.02),
    relation: 'composed_at', confidence: 'high',
    evidence: '诗题即登池上楼，永嘉郡治旧址今温州市区保有池上楼与谢池，是明确的创作场所。',
    sourceLabel: '《文选》卷二十二', sourceUrl,
    accent: '#8fc0a2', visualEffect: 'morning-rain', visualEffectLabel: '春草 · 鸣禽',
  },
  {
    id: 'xie-tiao-sanshan', title: '晚登三山还望京邑', author: '谢朓', dynasty: 'southern-northern',
    lines: [
      '灞涘望长安，河阳视京县。白日丽飞甍，参差皆可见。',
      '余霞散成绮，澄江静如练。喧鸟覆春洲，杂英满芳甸。',
      '去矣方滞淫，怀哉罢欢宴。佳期怅何许，泪下如流霰。',
      '有情知望乡，谁能鬒不变？',
    ],
    ...when(495, '495年春', '齐建武二年 · 出守宣城途中', 'circa', '谢朓建武二年出为宣城太守，自建康溯江西上，途经三山回望京邑，诗即作于此行。'),
    ...at('jiankang-sanshan', '建康西南 · 三山矶', 118.62, 31.93),
    relation: 'composed_at', confidence: 'high',
    evidence: '三山在建康西南长江边，今南京板桥附近三山矶，诗题与诗中澄江、京邑都指向此处。',
    sourceLabel: '《文选》卷二十七', sourceUrl,
    accent: '#d4a07a', visualEffect: 'river-mist', visualEffectLabel: '余霞 · 澄江',
  },
  {
    id: 'yu-xin-zhou-shangshu', title: '重别周尚书', author: '庾信', dynasty: 'southern-northern',
    lines: [
      '阳关万里道，不见一人归。',
      '惟有河边雁，秋来南向飞。',
    ],
    ...when(562, '约562年', '北周保定二年 · 周弘正南归', 'circa', '陈朝周弘正出使北周后南还，庾信羁留长安作诗二首相别，事在北周保定年间。'),
    ...at('beizhou-changan', '北周长安 · 庾信羁留馆舍', 108.9, 34.3),
    relation: 'composed_at', confidence: 'medium',
    evidence: '庾信出使西魏后被留长安，终身未归南朝；送别周弘正南归即在北周都城长安。',
    sourceLabel: '《庾子山集》卷四', sourceUrl,
    accent: '#a9b5c8', visualEffect: 'cloud-crane', visualEffectLabel: '阳关 · 南雁',
  },

  // 隋
  {
    id: 'yang-guang-ye-wang', title: '野望', author: '杨广', dynasty: 'sui',
    lines: [
      '寒鸦飞数点，流水绕孤村。',
      '斜阳欲落处，一望黯销魂。',
    ],
    ...when(610, '约605—616年', '大业年间 · 巡幸江都', 'range', '此诗以残句见于后世选本，一般与炀帝三下江都的大业年间相联，没有更具体的纪年材料。'),
    ...at('jiangdu-palace', '江都宫 · 临江殿', 119.42, 32.39),
    relation: 'associated', confidence: 'low',
    evidence: '炀帝晚年流连江都，以江都宫作为作者关联地；诗中孤村流水并未写出具体地名。',
    sourceLabel: '《全隋诗》', sourceUrl,
    accent: '#b58868', visualEffect: 'river-mist', visualEffectLabel: '寒鸦 · 孤村',
  },

  // 唐
  {
    id: 'wang-bo-tengwang-ge', title: '滕王阁诗', author: '王勃', dynasty: 'tang',
    lines: [
      '滕王高阁临江渚，佩玉鸣鸾罢歌舞。',
      '画栋朝飞南浦云，珠帘暮卷西山雨。',
      '闲云潭影日悠悠，物换星移几度秋。',
      '阁中帝子今何在？槛外长江空自流。',
    ],
    ...when(675, '675年重阳', '上元二年 · 洪州都督阎公宴', 'disputed', '此诗附于《滕王阁序》末，序作年有上元二年与咸亨二年等说，通行说法系于上元二年九月九日。'),
    ...at('hongzhou-tengwang', '洪州 · 滕王阁', 115.88, 28.68),
    relation: 'composed_at', confidence: 'high',
    evidence: '诗序皆写在滕王阁宴集席上，阁址临赣江，今南昌重建阁距唐阁旧址不远。',
    sourceLabel: '《王子安集》', sourceUrl,
    accent: '#c47b56', visualEffect: 'river-mist', visualEffectLabel: '画栋 · 珠帘',
  },
  {
    id: 'yang-jiong-cong-jun', title: '从军行', author: '杨炯', dynasty: 'tang',
    lines: [
      '烽火照西京，心中自不平。牙璋辞凤阙，铁骑绕龙城。',
      '雪暗凋旗画，风多杂鼓声。宁为百夫长，胜作一书生。',
    ],
    ...when(680, '约679—681年', '高宗调露、永隆年间', 'range', '调露、永隆年间吐蕃、突厥屡犯边，唐遣裴行俭等出征；杨炯时任崇文馆学士，诗多系于此间。'),
    ...at('changan-chongwenguan', '长安 · 东宫崇文馆', 108.95, 34.28),
    relation: 'associated', confidence: 'low',
    evidence: '杨炯并未从军，诗是在长安任崇文馆学士时代拟的乐府边塞题，故以东宫崇文馆作关联地。',
    sourceLabel: '《全唐诗》卷五十', sourceUrl,
    accent: '#b25a44', visualEffect: 'petals-embers', visualEffectLabel: '烽火 · 铁骑',
  },
  {
    id: 'zhang-ruoxu-chunjiang', title: '春江花月夜', author: '张若虚', dynasty: 'tang',
    lines: [
      '春江潮水连海平，海上明月共潮生。滟滟随波千万里，何处春江无月明！',
      '江流宛转绕芳甸，月照花林皆似霰。空里流霜不觉飞，汀上白沙看不见。',
      '江天一色无纤尘，皎皎空中孤月轮。江畔何人初见月？江月何年初照人？',
      '人生代代无穷已，江月年年望相似。不知江月待何人，但见长江送流水。',
      '白云一片去悠悠，青枫浦上不胜愁。谁家今夜扁舟子？何处相思明月楼？',
      '可怜楼上月徘徊，应照离人妆镜台。玉户帘中卷不去，捣衣砧上拂还来。',
      '此时相望不相闻，愿逐月华流照君。鸿雁长飞光不度，鱼龙潜跃水成文。',
      '昨夜闲潭梦落花，可怜春半不还家。江水流春去欲尽，江潭落月复西斜。',
      '斜月沉沉藏海雾，碣石潇湘无限路。不知乘月几人归，落月摇情满江树。',
    ],
    ...when(710, '约705—720年', '中宗至开元初年', 'period', '张若虚生平仅知为扬州人、中宗神龙中与贺知章等以文词俊秀驰名，本诗只能约定在这一时期。'),
    ...at('yangzhou-jiangbin', '扬州 · 江都江滨', 119.43, 32.3),
    relation: 'associated', confidence: 'low',
    evidence: '张若虚是扬州人，诗中江海相连的潮月景象与唐代扬州江岸相合，但诗未写具体地名。',
    sourceLabel: '《乐府诗集·清商曲辞》', sourceUrl,
    accent: '#a8bfd6', visualEffect: 'moon-fire', visualEffectLabel: '春江 · 海月',
  },
  {
    id: 'zhang-jiuling-wang-yue', title: '望月怀远', author: '张九龄', dynasty: 'tang',
    lines: [
      '海上生明月，天涯共此时。情人怨遥夜，竟夕起相思。',
      '灭烛怜光满，披衣觉露滋。不堪盈手赠，还寝梦佳期。',
    ],
    ...when(738, '约737—740年', '开元后期 · 贬荆州长史', 'range', '一般认为此诗作于张九龄罢相后出为荆州长史期间，与其同期《感遇》诗的心境相近。'),
    ...at('jingzhou-changshi', '荆州 · 江陵长史府', 112.24, 30.33),
    relation: 'associated', confidence: 'medium',
    evidence: '张九龄开元二十五年贬荆州长史，江陵是他这一时期的驻地，诗中没有更具体的地点。',
    sourceLabel: '《曲江集》', sourceUrl,
    accent: '#b8c8d8', visualEffect: 'moon-fire', visualEffectLabel: '海月 · 天涯',
  },
  {
    id: 'meng-haoran-xianshan', title: '与诸子登岘山', author: '孟浩然', dynasty: 'tang',
    lines: [
      '人事有代谢，往来成古今。江山留胜迹，我辈复登临。',
      '水落鱼梁浅，天寒梦泽深。羊公碑尚在，读罢泪沾襟。',
    ],
    ...when(725, '约开元中期', '孟浩然隐居襄阳期间', 'period', '诗写与友人登岘山读羊祜堕泪碑，孟浩然入京前后长期居襄阳，具体年份无题序可考。'),
    ...at('xiangyang-xianshan', '襄阳 · 岘山羊公碑', 112.16, 32.0),
    relation: 'composed_at', confidence: 'high',
    evidence: '岘山在襄阳城南，山上有纪念西晋羊祜的堕泪碑，诗中鱼梁洲也在附近汉水中。',
    sourceLabel: '《孟浩然集》卷三', sourceUrl,
    accent: '#8fa7a0', visualEffect: 'cloud-crane', visualEffectLabel: '岘山 · 羊碑',
  },
  {
    id: 'wang-wei-han-jiang', title: '汉江临泛', author: '王维', dynasty: 'tang',
    lines: [
      '楚塞三湘接，荆门九派通。江流天地外，山色有无中。',
      '郡邑浮前浦，波澜动远空。襄阳好风日，留醉与山翁。',
    ],
    ...when(740, '740年', '开元二十八年 · 知南选途经襄阳', 'circa', '王维开元二十八年以殿中侍御史知南选，南下途经襄阳，时孟浩然新卒，此诗一般系于此行。'),
    ...at('xiangyang-hanjiang', '襄阳 · 汉江舟中', 112.15, 32.05),
    relation: 'composed_at', confidence: 'medium',
    evidence: '诗题汉江临泛、末句襄阳好风日，说明是在襄阳段汉江上泛舟所作。',
    sourceLabel: '《王右丞集》卷八', sourceUrl,
    accent: '#8cc0b4', visualEffect: 'river-mist', visualEffectLabel: '江流 · 山色',
  },
  {
    id: 'wang-wei-zhongnan-shan', title: '终南山', author: '王维', dynasty: 'tang',
    lines: [
      '太乙近天都，连山接海隅。白云回望合，青霭入看无。',
      '分野中峰变，阴晴众壑殊。欲投人处宿，隔水问樵夫。',
    ],
    ...when(742, '约741—744年', '天宝初年 · 隐居终南', 'range', '王维开元末至天宝初曾隐居终南山，此诗多与他同期的《终南别业》一起系于这一时段。'),
    ...at('changan-zhongnan', '长安 · 终南山', 108.83, 33.95),
    relation: 'setting', confidence: 'high',
    evidence: '诗题与首句太乙都指终南山主峰一带，王维此时在山中有别业，是作品实写场景。',
    sourceLabel: '《王右丞集》卷七', sourceUrl,
    accent: '#9ec3b0', visualEffect: 'cloud-crane', visualEffectLabel: '白云 · 青霭',
  },
  {
    id: 'li-bai-yue-xia-du-zhuo', title: '月下独酌·其一', author: '李白', dynasty: 'tang',
    lines: [
      '花间一壶酒，独酌无相亲。举杯邀明月，对影成三人。',
      '月既不解饮，影徒随我身。暂伴月将影，行乐须及春。',
      '我歌月徘徊，我舞影零乱。醒时同交欢，醉后各分散。',
      '永结无情游，相期邈云汉。',
    ],
    ...when(744, '744年春', '天宝三载 · 翰林待诏末期', 'circa', '组诗其余篇有“长安”“三月咸阳城”等语，多系于天宝三载春李白在长安失意、即将去朝之时。'),
    ...at('changan-hanlin-farewell', '长安 · 翰林供奉饯行处', 108.95, 34.27),
    relation: 'composed_at', confidence: 'medium',
    evidence: '组诗第四首写三月咸阳城，可知作于长安，这里与李白翰林时期的长安地点合并显示。',
    sourceLabel: '《李太白全集》卷二十三', sourceUrl,
    accent: '#c3c9e0', visualEffect: 'moon-fire', visualEffectLabel: '独酌 · 邀月',
  },
  {
    id: 'li-bai-fenghuang-tai', title: '登金陵凤凰台', author: '李白', dynasty: 'tang',
    lines: [
      '凤凰台上凤凰游，凤去台空江自流。吴宫花草埋幽径，晋代衣冠成古丘。',
      '三山半落青天外，二水中分白鹭洲。总为浮云能蔽日，长安不见使人愁。',
    ],
    ...when(747, '约747年', '天宝六载 · 去朝后游金陵', 'disputed', '一说天宝六载去朝后游金陵所作，一说上元二年晚年再到金陵时作，均以“长安不见”的去国之感为据。'),
    ...at('jinling-fenghuangtai', '金陵 · 凤凰台', 118.77, 32.03),
    relation: 'composed_at', confidence: 'high',
    evidence: '凤凰台在金陵城西南隅，今南京凤台路一带，诗中三山、白鹭洲都在台西可望之处。',
    sourceLabel: '《李太白全集》卷二十一', sourceUrl,
    accent: '#d0b27a', visualEffect: 'river-flight', visualEffectLabel: '凤台 · 二水',
  },
  {
    id: 'li-bai-jiang-jin-jiu', title: '将进酒', author: '李白', dynasty: 'tang',
    lines: [
      '君不见黄河之水天上来，奔流到海不复回。君不见高堂明镜悲白发，朝如青丝暮成雪。',
      '人生得意须尽欢，莫使金樽空对月。天生我材必有用，千金散尽还复来。',
      '烹羊宰牛且为乐，会须一饮三百杯。岑夫子，丹丘生，将进酒，杯莫停。',
      '与君歌一曲，请君为我倾耳听。钟鼓馔玉不足贵，但愿长醉不愿醒。',
      '古来圣贤皆寂寞，惟有饮者留其名。陈王昔时宴平乐，斗酒十千恣欢谑。',
      '主人何为言少钱，径须沽取对君酌。五花马，千金裘，呼儿将出换美酒，与尔同销万古愁。',
    ],
    ...when(752, '约752年', '天宝十一载 · 与岑勋访元丹丘', 'disputed', '诗中岑夫子、丹丘生即岑勋、元丹丘，多数学者系于天宝十一载，也有开元年间初入长安前后之说。'),
    ...at('songshan-yingyang', '嵩山 · 颍阳元丹丘山居', 112.93, 34.42),
    relation: 'composed_at', confidence: 'medium',
    evidence: '元丹丘在嵩山、颍阳一带有山居，李白与岑勋同往访之，饮宴之地一般定在颍阳山居。',
    sourceLabel: '《李太白全集》卷三', sourceUrl,
    accent: '#d9a45b', visualEffect: 'waterfall', visualEffectLabel: '黄河 · 金樽',
  },
  {
    id: 'li-bai-xietiao-lou-jianbie', title: '宣州谢朓楼饯别校书叔云', author: '李白', dynasty: 'tang',
    lines: [
      '弃我去者，昨日之日不可留；乱我心者，今日之日多烦忧。',
      '长风万里送秋雁，对此可以酣高楼。蓬莱文章建安骨，中间小谢又清发。',
      '俱怀逸兴壮思飞，欲上青天览明月。抽刀断水水更流，举杯销愁愁更愁。',
      '人生在世不称意，明朝散发弄扁舟。',
    ],
    ...when(753, '753年秋', '天宝十二载 · 宣城', 'circa', '李白天宝十二载秋到宣城，李云时任秘书省校书郎来访而别，诗中秋雁与此行时令相合。'),
    ...at('xuancheng-xietiao', '宣城 · 谢朓楼寓居候选地', 118.75, 30.95),
    relation: 'composed_at', confidence: 'high',
    evidence: '谢朓楼是南齐谢朓任宣城太守时所建北楼，李白于楼上饯别，诗中小谢即指谢朓。',
    sourceLabel: '《李太白全集》卷十八', sourceUrl,
    accent: '#c7b8e0', visualEffect: 'cloud-crane', visualEffectLabel: '秋雁 · 高楼',
  },
  {
    id: 'li-bai-qiupu-15', title: '秋浦歌·其十五', author: '李白', dynasty: 'tang',
    lines: [
      '白发三千丈，缘愁似个长。',
      '不知明镜里，何处得秋霜。',
    ],
    ...when(754, '约754年', '天宝十三载 · 游秋浦', 'circa', '《秋浦歌》十七首一般系于天宝十三载前后李白游池州秋浦时，第十五首并无单独题序。'),
    ...at('chizhou-qiupu', '池州 · 秋浦河', 117.48, 30.63),
    relation: 'composed_at', confidence: 'medium',
    evidence: '秋浦是唐池州属县与河名，今池州市贵池区有秋浦河，组诗以此地命名。',
    sourceLabel: '《李太白全集》卷八', sourceUrl,
    accent: '#d8dde6', visualEffect: 'river-mist', visualEffectLabel: '白发 · 秋霜',
  },
  {
    id: 'du-fu-chun-ri-yi-li-bai', title: '春日忆李白', author: '杜甫', dynasty: 'tang',
    lines: [
      '白也诗无敌，飘然思不群。清新庾开府，俊逸鲍参军。',
      '渭北春天树，江东日暮云。何时一樽酒，重与细论文。',
    ],
    ...when(747, '约746—747年', '天宝五、六载 · 杜甫初入长安', 'circa', '杜甫天宝五载入长安，时李白已南游江东，诗中渭北与江东相对，多系于天宝五、六载春。'),
    ...at('changan-du-fu-lodging', '长安 · 杜甫旅食处', 108.94, 34.25),
    relation: 'composed_at', confidence: 'medium',
    evidence: '渭北春天树写杜甫所在的长安，江东日暮云写李白所在，两人一别后再未相见。',
    sourceLabel: '《杜工部集》卷九', sourceUrl,
    accent: '#a9c29b', visualEffect: 'cloud-crane', visualEffectLabel: '渭树 · 江云',
  },
  {
    id: 'du-fu-yue-ye', title: '月夜', author: '杜甫', dynasty: 'tang',
    lines: [
      '今夜鄜州月，闺中只独看。遥怜小儿女，未解忆长安。',
      '香雾云鬟湿，清辉玉臂寒。何时倚虚幌，双照泪痕干。',
    ],
    ...when(756, '756年秋', '至德元载 · 身陷长安', 'exact', '至德元载八月杜甫北投灵武途中被叛军俘至长安，妻儿留在鄜州羌村，此诗作于当年秋月夜。'),
    ...at('changan-fallen-city', '长安 · 安史陷城皇城东', 108.95, 34.27),
    relation: 'composed_at', confidence: 'high',
    evidence: '诗写身在长安遥想鄜州家人，与次年春的《春望》同在沦陷的长安城中。',
    sourceLabel: '《杜工部集》卷九', sourceUrl,
    accent: '#bfc8dc', visualEffect: 'moon-fire', visualEffectLabel: '鄜州月 · 泪痕',
  },
  {
    id: 'du-fu-shu-xiang', title: '蜀相', author: '杜甫', dynasty: 'tang',
    lines: [
      '丞相祠堂何处寻，锦官城外柏森森。映阶碧草自春色，隔叶黄鹂空好音。',
      '三顾频烦天下计，两朝开济老臣心。出师未捷身先死，长使英雄泪满襟。',
    ],
    ...when(760, '760年春', '上元元年 · 初到成都', 'circa', '杜甫乾元二年岁末抵成都，次年春营建草堂之际寻访诸葛亮祠堂，诗中碧草黄鹂正是春景。'),
    ...at('chengdu-wuhou-ci', '成都 · 武侯祠', 104.05, 30.65),
    relation: 'composed_at', confidence: 'high',
    evidence: '诗题蜀相、首联锦官城外柏森森，所写为成都城南的诸葛武侯祠，今祠址仍存。',
    sourceLabel: '《杜工部集》卷十三', sourceUrl,
    accent: '#8faf85', visualEffect: 'morning-rain', visualEffectLabel: '祠柏 · 黄鹂',
  },
  {
    id: 'du-fu-lv-ye-shu-huai', title: '旅夜书怀', author: '杜甫', dynasty: 'tang',
    lines: [
      '细草微风岸，危樯独夜舟。星垂平野阔，月涌大江流。',
      '名岂文章著，官应老病休。飘飘何所似，天地一沙鸥。',
    ],
    ...when(765, '765年', '永泰元年 · 离蜀东下', 'circa', '严武去世后杜甫携家离开成都，沿岷江、长江东下，诗多系于永泰元年途经渝州、忠州之际。'),
    ...at('zhongzhou-river', '忠州 · 长江夜泊', 108.04, 30.3),
    relation: 'route', confidence: 'medium',
    evidence: '诗中只写大江夜舟，具体泊处有渝州、忠州、云安诸说，此处以忠州江段作行旅节点。',
    sourceLabel: '《杜工部集》卷十四', sourceUrl,
    accent: '#a6b5cc', visualEffect: 'river-flight', visualEffectLabel: '星野 · 沙鸥',
  },
  {
    id: 'du-fu-deng-gao', title: '登高', author: '杜甫', dynasty: 'tang',
    lines: [
      '风急天高猿啸哀，渚清沙白鸟飞回。无边落木萧萧下，不尽长江滚滚来。',
      '万里悲秋常作客，百年多病独登台。艰难苦恨繁霜鬓，潦倒新停浊酒杯。',
    ],
    ...when(767, '767年重阳', '大历二年 · 夔州', 'exact', '杜甫大历元年至三年初寓居夔州，诗写重阳登高、因病停酒，历代注家系于大历二年秋。'),
    ...at('kuizhou-gaotai', '夔州 · 江畔高台', 109.55, 31.04),
    relation: 'composed_at', confidence: 'high',
    evidence: '猿啸与不尽长江都是夔州瞿塘峡口的景象，杜甫在夔州城外江边登高而作。',
    sourceLabel: '《杜工部集》卷十五', sourceUrl,
    accent: '#b89a72', visualEffect: 'river-flight', visualEffectLabel: '落木 · 长江',
  },
  {
    id: 'du-fu-yueyang-lou', title: '登岳阳楼', author: '杜甫', dynasty: 'tang',
    lines: [
      '昔闻洞庭水，今上岳阳楼。吴楚东南坼，乾坤日夜浮。',
      '亲朋无一字，老病有孤舟。戎马关山北，凭轩涕泗流。',
    ],
    ...when(768, '768年冬', '大历三年 · 出峡至岳州', 'exact', '杜甫大历三年正月出峡，辗转江陵、公安，岁暮抵岳州登楼；诗中戎马关山北指当年吐蕃侵扰。'),
    ...at('yueyang-tower', '岳州 · 岳阳楼', 113.09, 29.38),
    relation: 'composed_at', confidence: 'high',
    evidence: '诗题与首联都写登岳阳楼望洞庭，楼在岳州城西门上，是明确的创作场所。',
    sourceLabel: '《杜工部集》卷十八', sourceUrl,
    accent: '#8cb2c2', visualEffect: 'river-mist', visualEffectLabel: '洞庭 · 孤舟',
  },
  {
    id: 'cen-shen-qi-zhong', title: '碛中作', author: '岑参', dynasty: 'tang',
    lines: [
      '走马西来欲到天，辞家见月两回圆。',
      '今夜不知何处宿，平沙万里绝人烟。',
    ],
    ...when(749, '749年', '天宝八载 · 首次出塞赴安西', 'circa', '岑参天宝八载赴安西高仙芝幕，诗言辞家已两月，正是行至沙碛途中，系于首次西行。'),
    ...at('mohe-yanqi', '莫贺延碛 · 伊西道', 93.5, 41.5),
    relation: 'route', confidence: 'medium',
    evidence: '唐人所称碛西大沙碛指瓜州至伊州间的莫贺延碛，是赴安西必经之地，诗中未写具体驿名。',
    sourceLabel: '《岑嘉州诗》卷七', sourceUrl,
    accent: '#d4b27c', visualEffect: 'sun-river', visualEffectLabel: '平沙 · 两月',
  },
  {
    id: 'cui-hu-du-cheng-nan-zhuang', title: '题都城南庄', author: '崔护', dynasty: 'tang',
    lines: [
      '去年今日此门中，人面桃花相映红。',
      '人面不知何处去，桃花依旧笑春风。',
    ],
    ...when(796, '约796年前后', '贞元年间 · 崔护应举长安', 'circa', '《本事诗》记崔护举进士下第，清明独游都城南得此本事；崔护贞元十二年登第，诗约在其前后。'),
    ...at('changan-hanyu-south', '长安 · 城南樊川', 108.9, 34.05),
    relation: 'setting', confidence: 'medium',
    evidence: '题中都城南庄指长安城南郊村庄，《本事诗》所记即城南一带，具体庄院无法考定。',
    sourceLabel: '孟棨《本事诗·情感》', sourceUrl,
    accent: '#e0a0a0', visualEffect: 'petals-embers', visualEffectLabel: '人面 · 桃花',
  },
  {
    id: 'meng-jiao-deng-ke-hou', title: '登科后', author: '孟郊', dynasty: 'tang',
    lines: [
      '昔日龌龊不足夸，今朝放荡思无涯。',
      '春风得意马蹄疾，一日看尽长安花。',
    ],
    ...when(796, '796年春', '贞元十二年 · 进士放榜', 'exact', '孟郊两次落第后于贞元十二年登进士第，时年四十六岁，诗写放榜后的狂喜。'),
    ...at('changan-exam-quarter', '长安 · 进士省试坊', 108.95, 34.26),
    relation: 'composed_at', confidence: 'high',
    evidence: '诗写放榜当日走马看遍长安花，作于长安；与罗隐等人的科举诗同在进士坊聚合。',
    sourceLabel: '《孟东野诗集》卷三', sourceUrl,
    accent: '#e1b870', visualEffect: 'petals-embers', visualEffectLabel: '春风 · 马蹄',
  },
  {
    id: 'bai-juyi-guan-yi-mai', title: '观刈麦', author: '白居易', dynasty: 'tang',
    lines: [
      '田家少闲月，五月人倍忙。夜来南风起，小麦覆陇黄。',
      '妇姑荷箪食，童稚携壶浆。相随饷田去，丁壮在南冈。',
      '足蒸暑土气，背灼炎天光。力尽不知热，但惜夏日长。',
      '复有贫妇人，抱子在其旁。右手秉遗穗，左臂悬敝筐。',
      '听其相顾言，闻者为悲伤。家田输税尽，拾此充饥肠。',
      '今我何功德，曾不事农桑。吏禄三百石，岁晏有余粮。',
      '念此私自愧，尽日不能忘。',
    ],
    ...when(806, '806年五月', '元和元年 · 盩厔县尉', 'exact', '诗题下原注“时为盩厔县尉”，白居易元和元年任此职，诗写五月麦收，系年明确。'),
    ...at('zhouzhi-county', '盩厔 · 县尉官舍南冈', 108.22, 34.16),
    relation: 'composed_at', confidence: 'high',
    evidence: '自注说明作者身份是盩厔县尉，诗中南冈麦田即盩厔县境，今西安周至县。',
    sourceLabel: '《白氏长庆集》卷一', sourceUrl,
    accent: '#d5b56a', visualEffect: 'sun-river', visualEffectLabel: '麦陇 · 南风',
  },
  {
    id: 'bai-juyi-wen-liu-shi-jiu', title: '问刘十九', author: '白居易', dynasty: 'tang',
    lines: [
      '绿蚁新醅酒，红泥小火炉。',
      '晚来天欲雪，能饮一杯无？',
    ],
    ...when(817, '约817年冬', '元和十二年 · 江州司马任上', 'circa', '刘十九是白居易在江州结识的嵩阳处士刘轲，诗一般系于贬江州期间的冬日。'),
    ...at('jiangzhou-sima', '江州 · 浔阳司马宅', 115.99, 29.71),
    relation: 'composed_at', confidence: 'medium',
    evidence: '白居易元和十年至十三年在江州司马任，与刘十九往来于此；具体宅址已难考。',
    sourceLabel: '《白氏长庆集》卷十七', sourceUrl,
    accent: '#cf7c5d', visualEffect: 'petals-embers', visualEffectLabel: '新醅 · 火炉',
  },
  {
    id: 'yuan-zhen-wen-le-tian', title: '闻乐天授江州司马', author: '元稹', dynasty: 'tang',
    lines: [
      '残灯无焰影幢幢，此夕闻君谪九江。',
      '垂死病中惊坐起，暗风吹雨入寒窗。',
    ],
    ...when(815, '815年秋', '元和十年 · 通州司马任上', 'exact', '白居易元和十年八月贬江州司马，消息传到通州时元稹正患疟疾，诗即作于当年秋。'),
    ...at('tongzhou-yuan-zhen', '通州 · 元稹司马任所', 107.5, 31.21),
    relation: 'composed_at', confidence: 'high',
    evidence: '元稹同年三月已贬通州司马，今四川达州；诗写病中闻讯，作于通州任所。',
    sourceLabel: '《元氏长庆集》卷二十', sourceUrl,
    accent: '#9aa3b8', visualEffect: 'morning-rain', visualEffectLabel: '残灯 · 风雨',
  },
  {
    id: 'liu-yuxi-xisaishan', title: '西塞山怀古', author: '刘禹锡', dynasty: 'tang',
    lines: [
      '王濬楼船下益州，金陵王气黯然收。千寻铁锁沉江底，一片降幡出石头。',
      '人世几回伤往事，山形依旧枕寒流。今逢四海为家日，故垒萧萧芦荻秋。',
    ],
    ...when(824, '824年秋', '长庆四年 · 由夔州转和州途中', 'exact', '刘禹锡长庆四年由夔州刺史调任和州刺史，顺江东下途经西塞山，诗即作于此行。'),
    ...at('wuchang-xisaishan', '武昌 · 西塞山', 115.13, 30.2),
    relation: 'route', confidence: 'high',
    evidence: '此西塞山在今湖北黄石长江南岸，与湖州西塞山不同，是三国吴设防的江险。',
    sourceLabel: '《刘梦得文集》卷四', sourceUrl,
    accent: '#8aa2a8', visualEffect: 'river-flight', visualEffectLabel: '铁锁 · 寒流',
  },
  {
    id: 'liu-yuxi-wu-yi-xiang', title: '乌衣巷', author: '刘禹锡', dynasty: 'tang',
    lines: [
      '朱雀桥边野草花，乌衣巷口夕阳斜。',
      '旧时王谢堂前燕，飞入寻常百姓家。',
    ],
    ...when(826, '约826年', '宝历二年 · 和州刺史任上', 'circa', '《金陵五题》序言作者未到金陵，友人示以诗后追和；作于宝历年间和州任上，场景则是金陵。'),
    ...at('jinling-wuyi-xiang', '金陵 · 乌衣巷', 118.785, 32.022),
    relation: 'setting', confidence: 'high',
    evidence: '乌衣巷在秦淮河南岸朱雀桥边，东晋王导、谢安家族居此，诗写其地而非作者所在。',
    sourceLabel: '《刘梦得文集》卷四', sourceUrl,
    accent: '#c99471', visualEffect: 'river-mist', visualEffectLabel: '王谢 · 飞燕',
  },
  {
    id: 'liu-zongyuan-yu-weng', title: '渔翁', author: '柳宗元', dynasty: 'tang',
    lines: [
      '渔翁夜傍西岩宿，晓汲清湘燃楚竹。烟销日出不见人，欸乃一声山水绿。',
      '回看天际下中流，岩上无心云相逐。',
    ],
    ...when(809, '约809年', '元和四年前后 · 永州司马', 'circa', '诗与《始得西山宴游记》同写西山一带，一般系于元和四年柳宗元发现西山之后。'),
    ...at('yongzhou-yuxi', '永州 · 愚溪', 111.61, 26.42),
    relation: 'setting', confidence: 'medium',
    evidence: '西岩即永州西山，清湘指潇湘，与柳宗元贬居的愚溪同处永州城西。',
    sourceLabel: '《柳河东集》卷四十三', sourceUrl,
    accent: '#7eb7a0', visualEffect: 'river-mist', visualEffectLabel: '欸乃 · 山水',
  },
  {
    id: 'liu-zongyuan-liuzhou-lou', title: '登柳州城楼寄漳汀封连四州', author: '柳宗元', dynasty: 'tang',
    lines: [
      '城上高楼接大荒，海天愁思正茫茫。惊风乱飐芙蓉水，密雨斜侵薜荔墙。',
      '岭树重遮千里目，江流曲似九回肠。共来百越文身地，犹自音书滞一乡。',
    ],
    ...when(815, '815年夏秋', '元和十年 · 初到柳州', 'exact', '元和十年柳宗元与韩泰、韩晔、陈谏、刘禹锡同被外放漳、汀、封、连诸州，初到柳州登楼寄诗。'),
    ...at('liuzhou-chenglou', '柳州 · 城楼', 109.41, 24.33),
    relation: 'composed_at', confidence: 'high',
    evidence: '诗题明言登柳州城楼，城下柳江回环如九回肠，是柳宗元刺史任所。',
    sourceLabel: '《柳河东集》卷四十二', sourceUrl,
    accent: '#7ca58f', visualEffect: 'morning-rain', visualEffectLabel: '惊风 · 密雨',
  },
  {
    id: 'li-he-nan-yuan-5', title: '南园十三首·其五', author: '李贺', dynasty: 'tang',
    lines: [
      '男儿何不带吴钩，收取关山五十州。',
      '请君暂上凌烟阁，若个书生万户侯？',
    ],
    ...when(814, '约813—814年', '元和八、九年 · 辞奉礼郎归昌谷', 'range', '南园是李贺昌谷家中的园子，组诗写辞官归乡后的田园与愤懑，系于元和八、九年。'),
    ...at('yiyang-changgu', '宜阳 · 昌谷', 111.87, 34.51),
    relation: 'composed_at', confidence: 'medium',
    evidence: '南园在李贺故里福昌县昌谷，今河南宜阳，组诗多写园中景物。',
    sourceLabel: '《李长吉歌诗》卷一', sourceUrl,
    accent: '#a45a5a', visualEffect: 'petals-embers', visualEffectLabel: '吴钩 · 凌烟',
  },
  {
    id: 'du-mu-hua-qing-gong', title: '过华清宫绝句·其一', author: '杜牧', dynasty: 'tang',
    lines: [
      '长安回望绣成堆，山顶千门次第开。',
      '一骑红尘妃子笑，无人知是荔枝来。',
    ],
    ...when(838, '约836—839年', '开成年间 · 往来长安洛阳', 'range', '杜牧开成年间在长安、洛阳间任职往返，途经骊山华清宫旧址，组诗一般系于此时。'),
    ...at('lishan', '临潼 · 骊山', 109.22, 34.36),
    relation: 'setting', confidence: 'high',
    evidence: '华清宫在骊山北麓温泉，诗写山顶千门与自长安回望，与张养浩《骊山怀古》同地。',
    sourceLabel: '《樊川文集》卷二', sourceUrl,
    accent: '#d27d63', visualEffect: 'petals-embers', visualEffectLabel: '红尘 · 荔枝',
  },
  {
    id: 'du-mu-wu-jiang-ting', title: '题乌江亭', author: '杜牧', dynasty: 'tang',
    lines: [
      '胜败兵家事不期，包羞忍耻是男儿。',
      '江东子弟多才俊，卷土重来未可知。',
    ],
    ...when(844, '约844—846年', '会昌年间 · 池州刺史任上', 'range', '杜牧会昌四年起任池州刺史，途经和州乌江亭题诗，一般系于池州任期内。'),
    ...at('wujiang-pavilion', '和州 · 乌江亭', 118.04, 31.73),
    relation: 'composed_at', confidence: 'high',
    evidence: '乌江亭在和州乌江浦，即项羽自刎处，诗题写明题于亭上。',
    sourceLabel: '《樊川文集》卷四', sourceUrl,
    accent: '#b6694f', visualEffect: 'river-flight', visualEffectLabel: '乌江 · 卷土',
  },
  {
    id: 'du-mu-ji-yangzhou', title: '寄扬州韩绰判官', author: '杜牧', dynasty: 'tang',
    lines: [
      '青山隐隐水迢迢，秋尽江南草未凋。',
      '二十四桥明月夜，玉人何处教吹箫？',
    ],
    ...when(835, '约835年秋', '大和九年 · 离扬州入京后', 'circa', '杜牧大和七年至九年在扬州淮南节度使幕，此诗是离扬州后寄赠旧日同僚韩绰，约作于大和九年秋。'),
    ...at('yangzhou-ershisi-qiao', '扬州 · 二十四桥', 119.42, 32.4),
    relation: 'setting', confidence: 'high',
    evidence: '二十四桥是扬州城内桥梁的总称，诗写寄往扬州的想象场景；南宋姜夔扬州慢亦写此桥。',
    sourceLabel: '《樊川文集》卷四', sourceUrl,
    accent: '#bcc5dc', visualEffect: 'moon-fire', visualEffectLabel: '明月 · 吹箫',
  },
  {
    id: 'li-shangyin-le-you-yuan', title: '登乐游原', author: '李商隐', dynasty: 'tang',
    lines: [
      '向晚意不适，驱车登古原。',
      '夕阳无限好，只是近黄昏。',
    ],
    ...when(845, '约844—845年', '会昌年间 · 闲居长安', 'circa', '李商隐会昌年间因母丧居家，后返长安任秘书省正字，诗多系于会昌四、五年登乐游原时。'),
    ...at('changan-le-you-yuan', '长安 · 乐游原', 108.97, 34.23),
    relation: 'composed_at', confidence: 'high',
    evidence: '乐游原是长安城东南最高处，唐人登高望远之地，今西安青龙寺一带。',
    sourceLabel: '《李义山诗集》卷六', sourceUrl,
    accent: '#e0a36b', visualEffect: 'sun-river', visualEffectLabel: '夕阳 · 古原',
  },
  {
    id: 'wei-zhuang-tai-cheng', title: '台城', author: '韦庄', dynasty: 'tang',
    lines: [
      '江雨霏霏江草齐，六朝如梦鸟空啼。',
      '无情最是台城柳，依旧烟笼十里堤。',
    ],
    ...when(887, '约886—888年', '光启年间 · 避乱江南', 'circa', '韦庄黄巢乱后避居江南，往来润州、金陵间，此诗一般系于光启年间游金陵时。'),
    ...at('jinling-taicheng', '金陵 · 台城', 118.8, 32.07),
    relation: 'setting', confidence: 'high',
    evidence: '台城是六朝宫城所在，在今南京玄武湖南岸，诗中十里堤柳即宫城旧址外的堤岸。',
    sourceLabel: '《浣花集》卷四', sourceUrl,
    accent: '#8fb39b', visualEffect: 'morning-rain', visualEffectLabel: '江雨 · 台柳',
  },
  {
    id: 'huang-chao-ju-hua', title: '不第后赋菊', author: '黄巢', dynasty: 'tang',
    lines: [
      '待到秋来九月八，我花开后百花杀。',
      '冲天香阵透长安，满城尽带黄金甲。',
    ],
    ...when(870, '约860—874年', '懿宗、僖宗之际 · 黄巢屡试不第', 'period', '此诗首见于宋人笔记，一般认为是黄巢起事前应进士不第后所作，年代与真伪都只能约定。'),
    ...at('changan-exam-quarter', '长安 · 进士省试坊', 108.95, 34.26),
    relation: 'associated', confidence: 'low',
    evidence: '题称不第后作，与长安进士考试直接相关；同一地点也有孟郊登科后的狂喜。',
    sourceLabel: '《全唐诗》卷七三三', sourceUrl,
    accent: '#d4ad3f', visualEffect: 'petals-embers', visualEffectLabel: '黄菊 · 金甲',
  },

  // 五代
  {
    id: 'wei-zhuang-pu-sa-man', title: '菩萨蛮·人人尽说江南好', author: '韦庄', dynasty: 'five-dynasties',
    lines: [
      '人人尽说江南好，游人只合江南老。春水碧于天，画船听雨眠。',
      '垆边人似月，皓腕凝霜雪。未老莫还乡，还乡须断肠。',
    ],
    ...when(908, '约901—910年', '前蜀时期 · 追忆江南', 'disputed', '一说韦庄中年避乱江南时作，一说晚年仕前蜀时追忆江南；此处取后说，与其他四首菩萨蛮同系蜀中。'),
    ...at('chengdu-huanhua', '成都 · 浣花溪韦庄居所', 104.03, 30.665),
    relation: 'associated', confidence: 'low',
    evidence: '韦庄入蜀后寻得杜甫草堂旧址重葺而居，词集名浣花集；此处以其蜀中居所作关联地。',
    sourceLabel: '《花间集》卷二', sourceUrl,
    accent: '#8ec3c6', visualEffect: 'morning-rain', visualEffectLabel: '春水 · 画船',
  },
  {
    id: 'feng-yansi-ye-jin-men', title: '谒金门·风乍起', author: '冯延巳', dynasty: 'five-dynasties',
    lines: [
      '风乍起，吹皱一池春水。闲引鸳鸯香径里，手挼红杏蕊。',
      '斗鸭阑干独倚，碧玉搔头斜坠。终日望君君不至，举头闻鹊喜。',
    ],
    ...when(950, '约943—958年', '南唐保大年间', 'period', '马令《南唐书》记中主戏问“吹皱一池春水，干卿何事”，可知作于李璟在位时，具体年份不详。'),
    ...at('jinling-southern-tang', '金陵 · 南唐宫城澄心堂', 118.78, 32.06),
    relation: 'associated', confidence: 'low',
    evidence: '君臣以此词相戏见于南唐史籍，以金陵南唐宫城作关联地，与李璟、李煜词同处一地。',
    sourceLabel: '《阳春集》', sourceUrl,
    accent: '#9cc6a8', visualEffect: 'river-mist', visualEffectLabel: '春水 · 红杏',
  },
  {
    id: 'li-yu-po-zhen-zi', title: '破阵子·四十年来家国', author: '李煜', dynasty: 'five-dynasties',
    lines: [
      '四十年来家国，三千里地山河。凤阁龙楼连霄汉，玉树琼枝作烟萝，几曾识干戈？',
      '一旦归为臣虏，沈腰潘鬓消磨。最是仓皇辞庙日，教坊犹奏别离歌，垂泪对宫娥。',
    ],
    ...when(976, '约976年', '宋开宝九年 · 降宋北上前后', 'disputed', '词写辞庙之日，苏轼曾批评其临别不哭宗庙而对宫娥；作于降宋当时还是北迁后追忆，历来有异说。'),
    ...at('jinling-southern-tang', '金陵 · 南唐宫城澄心堂', 118.78, 32.06),
    relation: 'setting', confidence: 'medium',
    evidence: '仓皇辞庙发生在金陵南唐宫城，词写的是这一离别现场；此后李煜被押往汴京。',
    sourceLabel: '《南唐二主词》', sourceUrl,
    accent: '#b56d6d', visualEffect: 'petals-embers', visualEffectLabel: '辞庙 · 宫娥',
  },
  {
    id: 'li-yu-lang-tao-sha', title: '浪淘沙令·帘外雨潺潺', author: '李煜', dynasty: 'five-dynasties',
    lines: [
      '帘外雨潺潺，春意阑珊。罗衾不耐五更寒。梦里不知身是客，一晌贪欢。',
      '独自莫凭栏，无限江山，别时容易见时难。流水落花春去也，天上人间。',
    ],
    ...when(978, '约978年春', '宋太平兴国三年 · 汴京囚居', 'circa', '蔡絛《西清诗话》说此词作于李煜去世前不久，写北宋囚居的暮春，一般系于太平兴国三年。'),
    ...at('kaifeng-captivity', '汴京 · 违命侯宅', 114.31, 34.8),
    relation: 'composed_at', confidence: 'medium',
    evidence: '梦里身是客写被囚汴京的处境，与《虞美人》同在违命侯宅，具体宅第位置已不可考。',
    sourceLabel: '《南唐二主词》', sourceUrl,
    accent: '#8894b0', visualEffect: 'morning-rain', visualEffectLabel: '帘雨 · 落花',
  },

  // 宋
  {
    id: 'liu-yong-wang-hai-chao', title: '望海潮·东南形胜', author: '柳永', dynasty: 'song',
    lines: [
      '东南形胜，三吴都会，钱塘自古繁华。烟柳画桥，风帘翠幕，参差十万人家。',
      '云树绕堤沙，怒涛卷霜雪，天堑无涯。市列珠玑，户盈罗绮，竞豪奢。',
      '重湖叠巘清嘉，有三秋桂子，十里荷花。羌管弄晴，菱歌泛夜，嬉嬉钓叟莲娃。',
      '千骑拥高牙，乘醉听箫鼓，吟赏烟霞。异日图将好景，归去凤池夸。',
    ],
    ...when(1003, '约1003年', '咸平六年 · 柳永客居杭州', 'circa', '罗大经《鹤林玉露》说此词为谒两浙转运使孙何而作；孙何咸平年间知杭州，词约作于咸平末。'),
    ...at('hangzhou-xihu', '杭州 · 西湖', 120.15, 30.25),
    relation: 'setting', confidence: 'high',
    evidence: '重湖叠巘指西湖与环湖群山，怒涛指钱塘潮，词铺写北宋杭州全景。',
    sourceLabel: '《乐章集》', sourceUrl,
    accent: '#86b8a8', visualEffect: 'river-flight', visualEffectLabel: '桂子 · 荷花',
  },
  {
    id: 'liu-yong-yu-lin-ling', title: '雨霖铃·寒蝉凄切', author: '柳永', dynasty: 'song',
    lines: [
      '寒蝉凄切，对长亭晚，骤雨初歇。都门帐饮无绪，留恋处，兰舟催发。',
      '执手相看泪眼，竟无语凝噎。念去去，千里烟波，暮霭沉沉楚天阔。',
      '多情自古伤离别，更那堪，冷落清秋节！今宵酒醒何处？杨柳岸，晓风残月。',
      '此去经年，应是良辰好景虚设。便纵有千种风情，更与何人说？',
    ],
    ...when(1025, '约1024—1030年', '仁宗天圣年间 · 落第离京', 'range', '一般认为柳永屡试不第后离开汴京南下时作，词中都门帐饮与兰舟可证，年份只能约定在天圣年间。'),
    ...at('kaifeng-dumen', '汴京 · 都门外汴河长亭', 114.35, 34.79),
    relation: 'setting', confidence: 'medium',
    evidence: '都门帐饮说明在汴京城门外饯别，兰舟循汴河南下，故以城外汴河长亭为场景。',
    sourceLabel: '《乐章集》', sourceUrl,
    accent: '#9fb0c4', visualEffect: 'river-mist', visualEffectLabel: '长亭 · 残月',
  },
  {
    id: 'fan-zhongyan-su-mu-zhe', title: '苏幕遮·怀旧', author: '范仲淹', dynasty: 'song',
    lines: [
      '碧云天，黄叶地，秋色连波，波上寒烟翠。山映斜阳天接水，芳草无情，更在斜阳外。',
      '黯乡魂，追旅思，夜夜除非，好梦留人睡。明月楼高休独倚，酒入愁肠，化作相思泪。',
    ],
    ...when(1041, '约1040—1043年', '康定、庆历初 · 经略陕西', 'range', '词与《渔家傲·秋思》风格相近，一般系于范仲淹康定元年至庆历三年在陕西抵御西夏期间。'),
    ...at('yanan', '延州 · 延安', 109.49, 36.6),
    relation: 'associated', confidence: 'low',
    evidence: '范仲淹在西北边地任职时的乡思之作，以其驻守的延州作关联地，词中未写具体地点。',
    sourceLabel: '《范文正公诗余》', sourceUrl,
    accent: '#c7a765', visualEffect: 'river-mist', visualEffectLabel: '黄叶 · 寒烟',
  },
  {
    id: 'ouyang-xiu-xi-da-yuan-zhen', title: '戏答元珍', author: '欧阳修', dynasty: 'song',
    lines: [
      '春风疑不到天涯，二月山城未见花。残雪压枝犹有橘，冻雷惊笋欲抽芽。',
      '夜闻归雁生乡思，病入新年感物华。曾是洛阳花下客，野芳虽晚不须嗟。',
    ],
    ...when(1037, '1037年二月', '景祐四年 · 夷陵令任上', 'exact', '欧阳修景祐三年贬夷陵县令，次年早春答峡州判官丁宝臣（字元珍）诗，系年明确。'),
    ...at('yiling-county', '峡州 · 夷陵县署', 111.29, 30.7),
    relation: 'composed_at', confidence: 'high',
    evidence: '山城即夷陵，今湖北宜昌，在长江三峡出口；诗中橘与冻雷都是峡州早春风物。',
    sourceLabel: '《欧阳文忠公集》卷十一', sourceUrl,
    accent: '#a8bf8c', visualEffect: 'morning-rain', visualEffectLabel: '残雪 · 冻雷',
  },
  {
    id: 'wang-anshi-gui-zhi-xiang', title: '桂枝香·金陵怀古', author: '王安石', dynasty: 'song',
    lines: [
      '登临送目，正故国晚秋，天气初肃。千里澄江似练，翠峰如簇。归帆去棹残阳里，背西风，酒旗斜矗。彩舟云淡，星河鹭起，画图难足。',
      '念往昔，繁华竞逐，叹门外楼头，悲恨相续。千古凭高对此，谩嗟荣辱。六朝旧事随流水，但寒烟衰草凝绿。至今商女，时时犹唱，后庭遗曲。',
    ],
    ...when(1067, '约1067年', '治平四年 · 知江宁府', 'circa', '王安石治平四年知江宁府，词写晚秋登临金陵，多系于此；也有系于熙宁罢相后的说法。'),
    ...at('jinling-shitoucheng', '金陵 · 石头城', 118.75, 32.05),
    relation: 'setting', confidence: 'medium',
    evidence: '澄江似练化用谢朓诗句，登临所见为金陵西侧长江与城郭，石头城是历代登览处。',
    sourceLabel: '《临川先生文集》卷三十七', sourceUrl,
    accent: '#b9a47e', visualEffect: 'river-mist', visualEffectLabel: '澄江 · 翠峰',
  },
  {
    id: 'su-shi-jiang-cheng-zi-ji-meng', title: '江城子·乙卯正月二十日夜记梦', author: '苏轼', dynasty: 'song',
    lines: [
      '十年生死两茫茫，不思量，自难忘。千里孤坟，无处话凄凉。纵使相逢应不识，尘满面，鬓如霜。',
      '夜来幽梦忽还乡，小轩窗，正梳妆。相顾无言，惟有泪千行。料得年年肠断处，明月夜，短松冈。',
    ],
    ...when(1075, '1075年正月二十日', '熙宁八年 · 密州', 'exact', '题中乙卯正月二十日即熙宁八年，距妻王弗治平二年去世恰好十年，苏轼时知密州。'),
    ...at('mizhou-governor-house', '密州 · 知州官舍', 119.41, 35.99),
    relation: 'composed_at', confidence: 'high',
    evidence: '千里孤坟指眉山王弗墓，与作者所在的密州相距千里；词作于密州知州任上。',
    sourceLabel: '《东坡乐府》', sourceUrl,
    accent: '#aeb8cc', visualEffect: 'moon-fire', visualEffectLabel: '孤坟 · 松冈',
  },
  {
    id: 'su-shi-nian-nu-jiao', title: '念奴娇·赤壁怀古', author: '苏轼', dynasty: 'song',
    lines: [
      '大江东去，浪淘尽，千古风流人物。故垒西边，人道是，三国周郎赤壁。乱石穿空，惊涛拍岸，卷起千堆雪。江山如画，一时多少豪杰。',
      '遥想公瑾当年，小乔初嫁了，雄姿英发。羽扇纶巾，谈笑间，樯橹灰飞烟灭。故国神游，多情应笑我，早生华发。人生如梦，一尊还酹江月。',
    ],
    ...when(1082, '1082年七月', '元丰五年 · 黄州', 'circa', '词与同年七月既望所作《前赤壁赋》同游一地，一般系于元丰五年七月贬居黄州时。'),
    ...at('huangzhou-chibi', '黄州 · 赤鼻矶', 114.87, 30.44),
    relation: 'composed_at', confidence: 'high',
    evidence: '黄州城外赤鼻矶并非三国赤壁战场，苏轼以“人道是”存疑；杜牧《赤壁》也在此处。',
    sourceLabel: '《东坡乐府》', sourceUrl,
    accent: '#c9774f', visualEffect: 'river-flight', visualEffectLabel: '大江 · 千堆雪',
  },
  {
    id: 'su-shi-lin-jiang-xian', title: '临江仙·夜饮东坡醒复醉', author: '苏轼', dynasty: 'song',
    lines: [
      '夜饮东坡醒复醉，归来仿佛三更。家童鼻息已雷鸣。敲门都不应，倚杖听江声。',
      '长恨此身非我有，何时忘却营营。夜阑风静縠纹平。小舟从此逝，江海寄余生。',
    ],
    ...when(1082, '1082年九月', '元丰五年 · 黄州东坡雪堂', 'circa', '叶梦得《避暑录话》记苏轼作此词后郡守以为他已挂冠远去，事在元丰五年秋。'),
    ...at('huangzhou-dongpo', '黄州 · 东坡雪堂', 114.88, 30.45),
    relation: 'composed_at', confidence: 'high',
    evidence: '东坡是黄州城东的营地旧址，苏轼在此躬耕并筑雪堂，夜饮后回临皋亭寓所。',
    sourceLabel: '《东坡乐府》', sourceUrl,
    accent: '#96a9b8', visualEffect: 'river-mist', visualEffectLabel: '江声 · 小舟',
  },
  {
    id: 'su-shi-huizhou', title: '惠州一绝', author: '苏轼', dynasty: 'song',
    lines: [
      '罗浮山下四时春，卢橘杨梅次第新。',
      '日啖荔枝三百颗，不辞长作岭南人。',
    ],
    ...when(1096, '1096年', '绍圣三年 · 惠州', 'circa', '苏轼绍圣元年十月到惠州，此诗又题《食荔枝二首·其二》，一般系于绍圣三年荔枝成熟时。'),
    ...at('huizhou-hejiang', '惠州 · 合江楼白鹤峰', 114.41, 23.11),
    relation: 'composed_at', confidence: 'high',
    evidence: '罗浮山在惠州博罗，苏轼贬居惠州先后寓合江楼、筑白鹤峰新居，诗作于此期间。',
    sourceLabel: '《东坡续集》卷二', sourceUrl,
    accent: '#d0695d', visualEffect: 'sun-river', visualEffectLabel: '罗浮 · 荔枝',
  },
  {
    id: 'su-shi-du-hai', title: '六月二十日夜渡海', author: '苏轼', dynasty: 'song',
    lines: [
      '参横斗转欲三更，苦雨终风也解晴。云散月明谁点缀？天容海色本澄清。',
      '空余鲁叟乘桴意，粗识轩辕奏乐声。九死南荒吾不恨，兹游奇绝冠平生。',
    ],
    ...when(1100, '1100年六月二十日', '元符三年 · 遇赦北归', 'exact', '徽宗即位大赦，苏轼于元符三年六月二十日夜自儋州渡琼州海峡北归，题目记下了确切日期。'),
    ...at('qiongzhou-strait', '琼州海峡 · 澄迈渡', 110.2, 20.2),
    relation: 'route', confidence: 'high',
    evidence: '苏轼自澄迈驿渡海至雷州徐闻，诗写的就是海峡夜航，坐标取海峡中段。',
    sourceLabel: '《东坡后集》卷七', sourceUrl,
    accent: '#8fb4cc', visualEffect: 'moon-fire', visualEffectLabel: '天容 · 海色',
  },
  {
    id: 'su-shi-jin-shan-hua-xiang', title: '自题金山画像', author: '苏轼', dynasty: 'song',
    lines: [
      '心似已灰之木，身如不系之舟。',
      '问汝平生功业，黄州惠州儋州。',
    ],
    ...when(1101, '1101年五月', '建中靖国元年 · 北归过镇江', 'exact', '苏轼北归途经镇江金山寺，见李公麟所画旧像题诗，两月后卒于常州，系年明确。'),
    ...at('zhenjiang-jinshan', '镇江 · 金山寺', 119.42, 32.22),
    relation: 'composed_at', confidence: 'high',
    evidence: '题中金山即镇江金山寺，诗中列出的黄州、惠州、儋州正是苏轼三处贬所。',
    sourceLabel: '《东坡续集》卷二', sourceUrl,
    accent: '#c5b596', visualEffect: 'cloud-crane', visualEffectLabel: '不系舟 · 三州',
  },
  {
    id: 'huang-tingjian-kuai-ge', title: '登快阁', author: '黄庭坚', dynasty: 'song',
    lines: [
      '痴儿了却公家事，快阁东西倚晚晴。落木千山天远大，澄江一道月分明。',
      '朱弦已为佳人绝，青眼聊因美酒横。万里归船弄长笛，此心吾与白鸥盟。',
    ],
    ...when(1082, '1082年', '元丰五年 · 太和知县', 'circa', '黄庭坚元丰三年至六年知吉州太和县，快阁在县治东，诗一般系于元丰五年秋。'),
    ...at('taihe-kuaige', '太和 · 快阁', 114.9, 26.79),
    relation: 'composed_at', confidence: 'high',
    evidence: '快阁在太和县城东赣江边，今江西泰和，黄庭坚公事之余登阁而作。',
    sourceLabel: '《山谷诗集注》卷十一', sourceUrl,
    accent: '#a7bba3', visualEffect: 'river-mist', visualEffectLabel: '落木 · 白鸥',
  },
  {
    id: 'huang-tingjian-ji-huang-ji-fu', title: '寄黄几复', author: '黄庭坚', dynasty: 'song',
    lines: [
      '我居北海君南海，寄雁传书谢不能。桃李春风一杯酒，江湖夜雨十年灯。',
      '持家但有四立壁，治病不蕲三折肱。想得读书头已白，隔溪猿哭瘴溪藤。',
    ],
    ...when(1085, '1085年', '元丰八年 · 德州德平镇', 'exact', '原注“乙丑年德平镇作”，即元丰八年；黄几复时知广州四会县，所以说君在南海。'),
    ...at('dezhou-deping', '德州 · 德平镇', 116.9, 37.47),
    relation: 'composed_at', confidence: 'high',
    evidence: '作者自注作于德平镇，北宋属德州，靠近渤海，故自称居北海。',
    sourceLabel: '《山谷诗集注》卷二', sourceUrl,
    accent: '#c0a676', visualEffect: 'morning-rain', visualEffectLabel: '春风 · 夜雨',
  },
  {
    id: 'qin-guan-ta-suo-xing', title: '踏莎行·郴州旅舍', author: '秦观', dynasty: 'song',
    lines: [
      '雾失楼台，月迷津渡，桃源望断无寻处。可堪孤馆闭春寒，杜鹃声里斜阳暮。',
      '驿寄梅花，鱼传尺素，砌成此恨无重数。郴江幸自绕郴山，为谁流下潇湘去？',
    ],
    ...when(1097, '1097年春', '绍圣四年 · 郴州编管', 'exact', '秦观绍圣三年削秩编管郴州，词题郴州旅舍，写次年春孤馆所见，系于绍圣四年。'),
    ...at('chenzhou-lvshe', '郴州 · 旅舍', 113.02, 25.77),
    relation: 'composed_at', confidence: 'high',
    evidence: '词题与郴江、郴山都指郴州，苏轼曾将末两句写在扇上，感叹少游已矣。',
    sourceLabel: '《淮海居士长短句》', sourceUrl,
    accent: '#a9a6c4', visualEffect: 'river-mist', visualEffectLabel: '雾楼 · 郴江',
  },
  {
    id: 'zhou-bangyan-su-mu-zhe', title: '苏幕遮·燎沉香', author: '周邦彦', dynasty: 'song',
    lines: [
      '燎沉香，消溽暑。鸟雀呼晴，侵晓窥檐语。叶上初阳干宿雨，水面清圆，一一风荷举。',
      '故乡遥，何日去？家住吴门，久作长安旅。五月渔郎相忆否？小楫轻舟，梦入芙蓉浦。',
    ],
    ...when(1083, '约1078—1087年', '元丰年间 · 汴京太学', 'range', '词中久作长安旅指客居京城，一般认为是周邦彦元丰年间在汴京为太学生时的思乡之作。'),
    ...at('kaifeng-taixue', '汴京 · 太学', 114.34, 34.8),
    relation: 'associated', confidence: 'medium',
    evidence: '长安在宋词里泛指京城，周邦彦此时在汴京太学，故乡吴门即钱塘。',
    sourceLabel: '《片玉词》', sourceUrl,
    accent: '#7fbfa2', visualEffect: 'morning-rain', visualEffectLabel: '宿雨 · 风荷',
  },
  {
    id: 'li-qingzhao-yi-jian-mei', title: '一剪梅·红藕香残玉簟秋', author: '李清照', dynasty: 'song',
    lines: [
      '红藕香残玉簟秋。轻解罗裳，独上兰舟。云中谁寄锦书来？雁字回时，月满西楼。',
      '花自飘零水自流。一种相思，两处闲愁。此情无计可消除，才下眉头，却上心头。',
    ],
    ...when(1110, '约1107—1121年', '大观至宣和初 · 屏居青州', 'disputed', '元人伊世珍说此词为赵明诚负笈远游时所作，此说不可信；多数学者系于屏居青州、明诚出仕期间。'),
    ...at('qingzhou-guilaitang', '青州 · 归来堂', 118.48, 36.69),
    relation: 'associated', confidence: 'low',
    evidence: '李清照与赵明诚在青州屏居十年，自名其居为归来堂；词中别离相思与此期相合。',
    sourceLabel: '《漱玉词》', sourceUrl,
    accent: '#c7a2b0', visualEffect: 'moon-fire', visualEffectLabel: '锦书 · 西楼',
  },
  {
    id: 'li-qingzhao-wu-ling-chun', title: '武陵春·春晚', author: '李清照', dynasty: 'song',
    lines: [
      '风住尘香花已尽，日晚倦梳头。物是人非事事休，欲语泪先流。',
      '闻说双溪春尚好，也拟泛轻舟。只恐双溪舴艋舟，载不动许多愁。',
    ],
    ...when(1135, '1135年春', '绍兴五年 · 避乱金华', 'exact', '李清照绍兴四年冬避兵至金华，次年暮春作此词，此时赵明诚已去世六年。'),
    ...at('jinhua-shuangxi', '金华 · 双溪', 119.65, 29.08),
    relation: 'composed_at', confidence: 'high',
    evidence: '双溪是婺州城南东港、南港二水交汇处，今金华江畔，词中直接写出地名。',
    sourceLabel: '《漱玉词》', sourceUrl,
    accent: '#b7a3c0', visualEffect: 'river-mist', visualEffectLabel: '双溪 · 舴艋',
  },
  {
    id: 'li-qingzhao-sheng-sheng-man', title: '声声慢·寻寻觅觅', author: '李清照', dynasty: 'song',
    lines: [
      '寻寻觅觅，冷冷清清，凄凄惨惨戚戚。乍暖还寒时候，最难将息。三杯两盏淡酒，怎敌他、晚来风急！雁过也，正伤心，却是旧时相识。',
      '满地黄花堆积，憔悴损，如今有谁堪摘？守着窗儿，独自怎生得黑！梧桐更兼细雨，到黄昏、点点滴滴。这次第，怎一个愁字了得！',
    ],
    ...when(1140, '约1130—1150年', '南渡以后 · 流寓临安', 'range', '词写国破家亡、夫死以后的晚景，一般系于李清照南渡后寓居临安的晚年，没有确切年份。'),
    ...at('linan-li-qingzhao', '临安 · 李清照晚年寓所', 120.17, 30.25),
    relation: 'associated', confidence: 'low',
    evidence: '李清照晚年长期寓居临安，词中黄花梧桐是寓所庭院所见，具体住址无考。',
    sourceLabel: '《漱玉词》', sourceUrl,
    accent: '#c3a869', visualEffect: 'morning-rain', visualEffectLabel: '黄花 · 梧桐雨',
  },
  {
    id: 'yue-fei-cui-wei-ting', title: '池州翠微亭', author: '岳飞', dynasty: 'song',
    lines: [
      '经年尘土满征衣，特特寻芳上翠微。',
      '好水好山看不足，马蹄催趁月明归。',
    ],
    ...when(1135, '约1134—1136年', '绍兴初年 · 驻军池州', 'circa', '岳飞绍兴年间曾驻军池州，登齐山翠微亭；诗集真伪有争议，此首一般系于绍兴四至六年间。'),
    ...at('chizhou-cuiwei', '池州 · 齐山翠微亭', 117.5, 30.64),
    relation: 'composed_at', confidence: 'medium',
    evidence: '翠微亭在池州城南齐山，杜牧任池州刺史时所建，岳飞驻军时登临。',
    sourceLabel: '《岳武穆遗文》', sourceUrl,
    accent: '#8fb88e', visualEffect: 'cloud-crane', visualEffectLabel: '征衣 · 翠微',
  },
  {
    id: 'yue-fei-man-jiang-hong', title: '满江红·写怀', author: '岳飞', dynasty: 'song',
    lines: [
      '怒发冲冠，凭栏处、潇潇雨歇。抬望眼，仰天长啸，壮怀激烈。三十功名尘与土，八千里路云和月。莫等闲，白了少年头，空悲切。',
      '靖康耻，犹未雪。臣子恨，何时灭！驾长车，踏破贺兰山缺。壮志饥餐胡虏肉，笑谈渴饮匈奴血。待从头、收拾旧山河，朝天阙。',
    ],
    ...when(1136, '传为1133—1140年', '绍兴年间 · 北伐之际', 'disputed', '此词最早见于明代徐阶编《岳武穆遗文》，余嘉锡、夏承焘等疑为明人伪托，著作权与年代均有争议。'),
    ...at('ezhou-yuejia-jun', '鄂州 · 岳家军驻地', 114.31, 30.54),
    relation: 'associated', confidence: 'low',
    evidence: '岳家军以鄂州为大本营，据传词作于北伐前后；因著作权存疑，只标作人物关联地。',
    sourceLabel: '徐阶编《岳武穆遗文》', sourceUrl,
    accent: '#b0463a', visualEffect: 'petals-embers', visualEffectLabel: '长啸 · 山河',
  },
  {
    id: 'lu-you-jian-men', title: '剑门道中遇微雨', author: '陆游', dynasty: 'song',
    lines: [
      '衣上征尘杂酒痕，远游无处不消魂。',
      '此身合是诗人未？细雨骑驴入剑门。',
    ],
    ...when(1172, '1172年冬', '乾道八年 · 自南郑赴成都', 'exact', '王炎幕府解散后陆游改任成都府安抚司参议官，乾道八年十一月自南郑经剑门入蜀，途中作此诗。'),
    ...at('jianmen-pass', '剑州 · 剑门关', 105.56, 32.2),
    relation: 'route', confidence: 'high',
    evidence: '剑门是南郑至成都金牛道上的雄关，诗题写明途经剑门道，是行旅中的确定节点。',
    sourceLabel: '《剑南诗稿》卷三', sourceUrl,
    accent: '#9aab9c', visualEffect: 'morning-rain', visualEffectLabel: '征尘 · 细雨',
  },
  {
    id: 'lu-you-shu-fen', title: '书愤', author: '陆游', dynasty: 'song',
    lines: [
      '早岁那知世事艰，中原北望气如山。楼船夜雪瓜洲渡，铁马秋风大散关。',
      '塞上长城空自许，镜中衰鬓已先斑。出师一表真名世，千载谁堪伯仲间！',
    ],
    ...when(1186, '1186年春', '淳熙十三年 · 山阴', 'exact', '诗作于淳熙十三年春，陆游时年六十二岁，闲居山阴，诗中回忆瓜洲与大散关旧事。'),
    ...at('shaoxing-shanyin', '山阴 · 三山别业', 120.58, 30),
    relation: 'composed_at', confidence: 'high',
    evidence: '陆游此年仍在山阴家居，诗中瓜洲渡、大散关是回忆中的地点，并非写作地。',
    sourceLabel: '《剑南诗稿》卷十七', sourceUrl,
    accent: '#b58c6a', visualEffect: 'sun-river', visualEffectLabel: '楼船 · 铁马',
  },
  {
    id: 'lu-you-lin-an-chun-yu', title: '临安春雨初霁', author: '陆游', dynasty: 'song',
    lines: [
      '世味年来薄似纱，谁令骑马客京华？小楼一夜听春雨，深巷明朝卖杏花。',
      '矮纸斜行闲作草，晴窗细乳戏分茶。素衣莫起风尘叹，犹及清明可到家。',
    ],
    ...when(1186, '1186年春', '淳熙十三年 · 候召临安', 'exact', '陆游淳熙十三年春被召赴临安，等候召见期间寓居西湖边客邸，诗与《书愤》同年。'),
    ...at('linan-zhuanjie', '临安 · 砖街巷客邸', 120.17, 30.26),
    relation: 'composed_at', confidence: 'high',
    evidence: '陆游此行寓居临安砖街巷南的客邸小楼，诗题即说明在临安。',
    sourceLabel: '《剑南诗稿》卷十七', sourceUrl,
    accent: '#d5a1a8', visualEffect: 'morning-rain', visualEffectLabel: '春雨 · 杏花',
  },
  {
    id: 'lu-you-shen-yuan', title: '沈园二首·其一', author: '陆游', dynasty: 'song',
    lines: [
      '城上斜阳画角哀，沈园非复旧池台。',
      '伤心桥下春波绿，曾是惊鸿照影来。',
    ],
    ...when(1199, '1199年春', '庆元五年 · 七十五岁重游', 'exact', '陆游庆元五年重游沈园，距与唐琬相遇已四十余年，唐琬早已去世，诗题下有年份记载。'),
    ...at('shaoxing-shenyuan', '山阴 · 沈园', 120.59, 30.0),
    relation: 'composed_at', confidence: 'high',
    evidence: '沈园在绍兴城东南，今仍存，是陆游与唐琬重逢之地，诗写重游园中所见。',
    sourceLabel: '《剑南诗稿》卷三十八', sourceUrl,
    accent: '#9fc0a4', visualEffect: 'river-mist', visualEffectLabel: '春波 · 惊鸿',
  },
  {
    id: 'fan-chengda-zhou-qiao', title: '州桥', author: '范成大', dynasty: 'song',
    lines: [
      '州桥南北是天街，父老年年等驾回。',
      '忍泪失声询使者：几时真有六军来？',
    ],
    ...when(1170, '1170年秋', '乾道六年 · 使金途经汴京', 'exact', '范成大乾道六年出使金国，途中写下七十二首绝句，本首作于经过已沦陷四十余年的汴京时。'),
    ...at('kaifeng-zhouqiao', '汴京 · 州桥', 114.35, 34.79),
    relation: 'composed_at', confidence: 'high',
    evidence: '州桥是汴河上正对宫城御街的桥，诗中天街即御街，范成大使金时亲历此地。',
    sourceLabel: '《石湖诗集》卷十二', sourceUrl,
    accent: '#b99570', visualEffect: 'sun-river', visualEffectLabel: '天街 · 父老',
  },
  {
    id: 'xin-qiji-yong-yu-le', title: '永遇乐·京口北固亭怀古', author: '辛弃疾', dynasty: 'song',
    lines: [
      '千古江山，英雄无觅，孙仲谋处。舞榭歌台，风流总被雨打风吹去。斜阳草树，寻常巷陌，人道寄奴曾住。想当年，金戈铁马，气吞万里如虎。',
      '元嘉草草，封狼居胥，赢得仓皇北顾。四十三年，望中犹记，烽火扬州路。可堪回首，佛狸祠下，一片神鸦社鼓。凭谁问：廉颇老矣，尚能饭否？',
    ],
    ...when(1205, '1205年', '开禧元年 · 知镇江府', 'exact', '词中四十三年指绍兴三十二年南归至开禧元年，时辛弃疾六十六岁知镇江府，系年明确。'),
    ...at('zhenjiang-beigu', '京口 · 北固亭', 119.43, 32.21),
    relation: 'composed_at', confidence: 'high',
    evidence: '北固亭在镇江北固山上，与他的《南乡子·登京口北固亭有怀》作于同一地点。',
    sourceLabel: '《稼轩长短句》', sourceUrl,
    accent: '#b56a48', visualEffect: 'petals-embers', visualEffectLabel: '铁马 · 廉颇',
  },
  {
    id: 'xin-qiji-zao-kou', title: '菩萨蛮·书江西造口壁', author: '辛弃疾', dynasty: 'song',
    lines: [
      '郁孤台下清江水，中间多少行人泪。西北望长安，可怜无数山。',
      '青山遮不住，毕竟东流去。江晚正愁余，山深闻鹧鸪。',
    ],
    ...when(1176, '1176年', '淳熙三年 · 江西提点刑狱', 'exact', '辛弃疾淳熙三年任江西提点刑狱，驻赣州，巡行途经万安造口题壁，追怀建炎年间隆祐太后被金兵追至此处。'),
    ...at('wanan-zaokou', '万安 · 造口', 114.83, 26.37),
    relation: 'composed_at', confidence: 'high',
    evidence: '题中造口即皂口，在今江西万安西南赣江边；词中郁孤台在赣州，是上游回望之地。',
    sourceLabel: '《稼轩长短句》', sourceUrl,
    accent: '#86a9a0', visualEffect: 'river-flight', visualEffectLabel: '清江 · 鹧鸪',
  },
  {
    id: 'xin-qiji-qing-yu-an', title: '青玉案·元夕', author: '辛弃疾', dynasty: 'song',
    lines: [
      '东风夜放花千树，更吹落、星如雨。宝马雕车香满路。凤箫声动，玉壶光转，一夜鱼龙舞。',
      '蛾儿雪柳黄金缕，笑语盈盈暗香去。众里寻他千百度，蓦然回首，那人却在，灯火阑珊处。',
    ],
    ...when(1174, '约1174—1175年', '淳熙初年 · 仕于临安', 'circa', '词写都城元宵灯市，一般认为是辛弃疾淳熙元年至二年在临安任仓部郎官时所作。'),
    ...at('linan-yujie', '临安 · 御街灯市', 120.17, 30.25),
    relation: 'setting', confidence: 'medium',
    evidence: '宝马雕车、鱼龙舞写南宋临安御街元宵灯会，《武林旧事》对此有详细记载。',
    sourceLabel: '《稼轩长短句》', sourceUrl,
    accent: '#e0b35e', visualEffect: 'petals-embers', visualEffectLabel: '花千树 · 灯火',
  },
  {
    id: 'jiang-kui-yangzhou-man', title: '扬州慢·淮左名都', author: '姜夔', dynasty: 'song',
    lines: [
      '淮左名都，竹西佳处，解鞍少驻初程。过春风十里，尽荠麦青青。自胡马窥江去后，废池乔木，犹厌言兵。渐黄昏，清角吹寒，都在空城。',
      '杜郎俊赏，算而今、重到须惊。纵豆蔻词工，青楼梦好，难赋深情。二十四桥仍在，波心荡、冷月无声。念桥边红药，年年知为谁生？',
    ],
    ...when(1176, '1176年冬至', '淳熙三年 · 丙申至日过维扬', 'exact', '词序自称“淳熙丙申至日，予过维扬”，距金兵完颜亮南侵扬州已十五年，系年明确。'),
    ...at('yangzhou-ershisi-qiao', '扬州 · 二十四桥', 119.42, 32.4),
    relation: 'composed_at', confidence: 'high',
    evidence: '词中杜郎即杜牧，二十四桥、豆蔻、青楼皆化用杜牧扬州诗，与杜牧诗同在一桥。',
    sourceLabel: '《白石道人歌曲》卷四', sourceUrl,
    accent: '#a5b1c9', visualEffect: 'moon-fire', visualEffectLabel: '空城 · 冷月',
  },
  {
    id: 'jiang-jie-yi-jian-mei', title: '一剪梅·舟过吴江', author: '蒋捷', dynasty: 'song',
    lines: [
      '一片春愁待酒浇。江上舟摇，楼上帘招。秋娘渡与泰娘桥，风又飘飘，雨又萧萧。',
      '何日归家洗客袍？银字笙调，心字香烧。流光容易把人抛，红了樱桃，绿了芭蕉。',
    ],
    ...when(1276, '约1276年', '德祐二年 · 宋亡之际流离', 'circa', '蒋捷咸淳十年进士，不久宋亡，此后漂泊太湖一带；词多系于德祐年间元军南下时。'),
    ...at('suzhou-wujiang-waterway', '苏州 · 吴江水道', 120.64, 31.16),
    relation: 'route', confidence: 'medium',
    evidence: '秋娘渡、泰娘桥都是吴江地名，词题写明舟过吴江，与杨万里等人的吴江诗共享此水道。',
    sourceLabel: '《竹山词》', sourceUrl,
    accent: '#c86a74', visualEffect: 'morning-rain', visualEffectLabel: '樱桃 · 芭蕉',
  },

  // 元
  {
    id: 'guan-hanqing-si-kuai-yu', title: '四块玉·闲适', author: '关汉卿', dynasty: 'yuan',
    lines: [
      '旧酒投，新醅泼，老瓦盆边笑呵呵，共山僧野叟闲吟和。',
      '他出一对鸡，我出一个鹅，闲快活！',
    ],
    ...when(1280, '约1275—1300年', '至元至大德年间', 'period', '关汉卿生卒年不详，大体活动于元初，散曲没有纪年，只能约定在他创作的主要时期。'),
    ...at('dadu-yujing-shuhui', '大都 · 玉京书会', 116.38, 39.93),
    relation: 'associated', confidence: 'low',
    evidence: '关汉卿是大都人，曾为玉京书会才人，以大都书会作为散曲作者的活动关联地。',
    sourceLabel: '《全元散曲》', sourceUrl,
    accent: '#c7a068', visualEffect: 'petals-embers', visualEffectLabel: '瓦盆 · 鸡鹅',
  },
  {
    id: 'bai-pu-tian-jing-sha-qiu', title: '天净沙·秋', author: '白朴', dynasty: 'yuan',
    lines: [
      '孤村落日残霞，轻烟老树寒鸦，一点飞鸿影下。',
      '青山绿水，白草红叶黄花。',
    ],
    ...when(1285, '约1280—1300年', '元初 · 白朴晚年寓居金陵', 'period', '白朴晚年徙居建康，散曲多无纪年；这首小令只能按其后期创作约定在至元、大德之间。'),
    ...at('jinling-baipu', '金陵 · 白朴寓居处', 118.78, 32.05),
    relation: 'associated', confidence: 'low',
    evidence: '白朴原籍隩州，金亡后流寓真定，晚年定居建康，以金陵作为作者后期关联地。',
    sourceLabel: '《天籁集》', sourceUrl,
    accent: '#d19a62', visualEffect: 'cloud-crane', visualEffectLabel: '寒鸦 · 飞鸿',
  },
  {
    id: 'sadula-man-jiang-hong', title: '满江红·金陵怀古', author: '萨都剌', dynasty: 'yuan',
    lines: [
      '六代豪华，春去也、更无消息。空怅望，山川形胜，已非畴昔。王谢堂前双燕子，乌衣巷口曾相识。听夜深，寂寞打孤城，春潮急。',
      '思往事，愁如织。怀故国，空陈迹。但荒烟衰草，乱鸦斜日。玉树歌残秋露冷，胭脂井坏寒螀泣。到如今，只有蒋山青，秦淮碧！',
    ],
    ...when(1330, '约1329—1332年', '天历、至顺年间 · 江南行台', 'circa', '萨都剌天历年间任江南行御史台掾史，驻金陵，这首怀古词一般系于此时。'),
    ...at('jinling-wuyi-xiang', '金陵 · 乌衣巷', 118.785, 32.022),
    relation: 'setting', confidence: 'medium',
    evidence: '词中直接化用刘禹锡乌衣巷诗句，蒋山、秦淮都在金陵，故与刘禹锡诗同地显示。',
    sourceLabel: '《雁门集》', sourceUrl,
    accent: '#a09a78', visualEffect: 'river-mist', visualEffectLabel: '秦淮 · 蒋山',
  },
  {
    id: 'wang-mian-bai-mei', title: '白梅', author: '王冕', dynasty: 'yuan',
    lines: [
      '冰雪林中著此身，不同桃李混芳尘。',
      '忽然一夜清香发，散作乾坤万里春。',
    ],
    ...when(1352, '约1350—1359年', '至正年间 · 隐居九里山', 'range', '王冕至正年间北游归来后隐居会稽九里山，种梅千株，咏梅诗多系于此时。'),
    ...at('zhuji', '诸暨 · 九里山', 120.23, 29.71),
    relation: 'associated', confidence: 'medium',
    evidence: '王冕在九里山筑梅花屋，与他的《墨梅》同地；诗中梅林即隐居处风物。',
    sourceLabel: '《竹斋集》', sourceUrl,
    accent: '#e3e3dc', visualEffect: 'cloud-crane', visualEffectLabel: '冰雪 · 清香',
  },

  // 明
  {
    id: 'gao-qi-mei-hua', title: '梅花九首·其一', author: '高启', dynasty: 'ming',
    lines: [
      '琼姿只合在瑶台，谁向江南处处栽？雪满山中高士卧，月明林下美人来。',
      '寒依疏影萧萧竹，春掩残香漠漠苔。自去何郎无好咏，东风愁寂几回开。',
    ],
    ...when(1369, '约1368—1370年', '洪武初年 · 居吴淞青丘', 'circa', '高启元末隐居吴淞江畔青丘，洪武初应召修元史，梅花诗一般系于洪武初年前后。'),
    ...at('changzhou-qingqiu', '长洲 · 吴淞江青丘', 120.9, 31.35),
    relation: 'associated', confidence: 'low',
    evidence: '高启自号青丘子，青丘在长洲县吴淞江畔，这里作为作者隐居关联地。',
    sourceLabel: '《高太史大全集》', sourceUrl,
    accent: '#e2d6d6', visualEffect: 'moon-fire', visualEffectLabel: '雪山 · 月林',
  },
  {
    id: 'yu-qian-yong-mei-tan', title: '咏煤炭', author: '于谦', dynasty: 'ming',
    lines: [
      '凿开混沌得乌金，藏蓄阳和意最深。爝火燃回春浩浩，洪炉照破夜沉沉。',
      '鼎彝元赖生成力，铁石犹存死后心。但愿苍生俱饱暖，不辞辛苦出山林。',
    ],
    ...when(1435, '约1430—1448年', '宣德、正统年间 · 巡抚山西', 'range', '山西产煤，于谦宣德五年起巡抚河南、山西十八年，此诗一般系于巡抚山西期间。'),
    ...at('taiyuan-yuqian', '太原 · 于谦巡抚行署', 112.55, 37.87),
    relation: 'associated', confidence: 'low',
    evidence: '于谦巡抚山西时往来太原，诗咏晋地煤炭；具体写作地点没有记载。',
    sourceLabel: '《忠肃集》', sourceUrl,
    accent: '#c56b4c', visualEffect: 'petals-embers', visualEffectLabel: '乌金 · 洪炉',
  },
  {
    id: 'tang-yin-tao-hua-an', title: '桃花庵歌', author: '唐寅', dynasty: 'ming',
    lines: [
      '桃花坞里桃花庵，桃花庵里桃花仙。桃花仙人种桃树，又摘桃花换酒钱。',
      '酒醒只在花前坐，酒醉还来花下眠。半醒半醉日复日，花落花开年复年。',
      '但愿老死花酒间，不愿鞠躬车马前。车尘马足富者趣，酒盏花枝贫者缘。',
      '若将富贵比贫贱，一在平地一在天。若将贫贱比车马，他得驱驰我得闲。',
      '别人笑我太疯癫，我笑他人看不穿。不见五陵豪杰墓，无花无酒锄作田。',
    ],
    ...when(1505, '1505年', '弘治十八年 · 筑桃花庵', 'circa', '唐寅弘治十八年于苏州城北桃花坞筑桃花庵，此歌通常系于庵成之时。'),
    ...at('suzhou-taohuawu', '苏州 · 桃花坞', 120.62, 31.3),
    relation: 'composed_at', confidence: 'high',
    evidence: '桃花坞在苏州城北，唐寅在此筑庵自号桃花庵主，与他的《画鸡》同处。',
    sourceLabel: '《唐伯虎全集》', sourceUrl,
    accent: '#e39aa6', visualEffect: 'petals-embers', visualEffectLabel: '桃花 · 酒钱',
  },
  {
    id: 'qi-jiguang-ma-shang-zuo', title: '马上作', author: '戚继光', dynasty: 'ming',
    lines: [
      '南北驱驰报主情，江花边草笑平生。',
      '一年三百六十日，多是横戈马上行。',
    ],
    ...when(1575, '约1568—1582年', '隆庆、万历初 · 镇守蓟州', 'range', '诗中南北驱驰概括了戚继光先在浙闽平倭、后镇蓟州的经历，一般系于北调蓟镇以后。'),
    ...at('jizhou-zhen', '蓟州 · 蓟镇总兵府', 117.41, 40.05),
    relation: 'associated', confidence: 'medium',
    evidence: '诗写北调后的边地生涯，蓟州是戚继光镇守十六年的总兵驻地，边草即长城一线。',
    sourceLabel: '《止止堂集》', sourceUrl,
    accent: '#b9895c', visualEffect: 'sun-river', visualEffectLabel: '江花 · 边草',
  },

  // 清
  {
    id: 'gu-yanwu-jing-wei', title: '精卫', author: '顾炎武', dynasty: 'qing',
    lines: [
      '万事有不平，尔何空自苦；长将一寸身，衔木到终古？',
      '我愿平东海，身沉心不改；大海无平期，我心无绝时。',
      '呜呼！君不见，西山衔木众鸟多，鹊来燕去自成窠。',
    ],
    ...when(1647, '1647年', '顺治四年 · 抗清失败后', 'circa', '诗以精卫填海自喻抗清之志，据徐嘉《顾诗笺注》系于顺治四年，时昆山抗清已败。'),
    ...at('kunshan-tinglin', '昆山 · 千灯亭林故居', 120.98, 31.26),
    relation: 'associated', confidence: 'low',
    evidence: '顾炎武是昆山千灯人，母亲在昆山城破后绝食殉国，此诗与其家乡的抗清经历相关。',
    sourceLabel: '《亭林诗集》卷一', sourceUrl,
    accent: '#8aa2b0', visualEffect: 'river-flight', visualEffectLabel: '衔木 · 东海',
  },
  {
    id: 'wang-shizhen-zhenzhou', title: '真州绝句·其四', author: '王士禛', dynasty: 'qing',
    lines: [
      '江干多是钓人居，柳陌菱塘一带疏。',
      '好是日斜风定后，半江红树卖鲈鱼。',
    ],
    ...when(1662, '约1661—1664年', '康熙初年 · 扬州推官', 'circa', '王士禛顺治十七年至康熙四年任扬州推官，真州属扬州府，组诗一般系于此间的江行。'),
    ...at('zhenzhou-jiangan', '真州 · 江干', 119.18, 32.27),
    relation: 'setting', confidence: 'high',
    evidence: '真州即今江苏仪征，濒临长江，诗写江边渔家晚景，诗题明确地名。',
    sourceLabel: '《渔洋山人精华录》', sourceUrl,
    accent: '#cc7a5a', visualEffect: 'sun-river', visualEffectLabel: '红树 · 鲈鱼',
  },
  {
    id: 'nalan-mu-lan-hua', title: '木兰花·拟古决绝词柬友', author: '纳兰性德', dynasty: 'qing',
    lines: [
      '人生若只如初见，何事秋风悲画扇。等闲变却故人心，却道故人心易变。',
      '骊山语罢清宵半，泪雨霖铃终不怨。何如薄幸锦衣郎，比翼连枝当日愿。',
    ],
    ...when(1678, '约1675—1684年', '康熙中期 · 侍卫京师', 'period', '词题说是寄给友人的拟古之作，词中没有纪年线索，只能约定在纳兰任侍卫的康熙中期。'),
    ...at('beijing-lushui-ting', '京师 · 明珠府渌水亭', 116.38, 39.94),
    relation: 'associated', confidence: 'low',
    evidence: '纳兰性德常在什刹海畔的渌水亭与友人唱和，以此作为寄友词的关联地。',
    sourceLabel: '《饮水词》', sourceUrl,
    accent: '#b8a2c4', visualEffect: 'moon-fire', visualEffectLabel: '画扇 · 连枝',
  },
  {
    id: 'zheng-xie-weixian-zhu', title: '潍县署中画竹呈年伯包大中丞括', author: '郑燮', dynasty: 'qing',
    lines: [
      '衙斋卧听萧萧竹，疑是民间疾苦声。',
      '些小吾曹州县吏，一枝一叶总关情。',
    ],
    ...when(1746, '约1746—1748年', '乾隆十一年后 · 潍县知县', 'circa', '郑燮乾隆十一年调任潍县，适逢大灾；包括时任山东巡抚，题画诗约作于任潍县知县之初。'),
    ...at('weixian-yamen', '潍县 · 县署衙斋', 119.1, 36.7),
    relation: 'composed_at', confidence: 'high',
    evidence: '诗题写明作于潍县署中，衙斋即知县官舍，今山东潍坊。',
    sourceLabel: '《郑板桥集》', sourceUrl,
    accent: '#8fbf8a', visualEffect: 'morning-rain', visualEffectLabel: '衙竹 · 民声',
  },
  {
    id: 'huang-jingren-chuxi', title: '癸巳除夕偶成·其一', author: '黄景仁', dynasty: 'qing',
    lines: [
      '千家笑语漏迟迟，忧患潜从物外知。',
      '悄立市桥人不识，一星如月看多时。',
    ],
    ...when(1773, '1773年除夕', '乾隆三十八年 · 癸巳', 'exact', '题中癸巳即乾隆三十八年，黄景仁时年二十五岁，除夕归常州家中作。'),
    ...at('changzhou-shiqiao', '常州 · 市桥', 119.96, 31.78),
    relation: 'composed_at', confidence: 'medium',
    evidence: '黄景仁是常州武进人，除夕在家乡街市桥头独立，具体桥名不可考。',
    sourceLabel: '《两当轩集》卷六', sourceUrl,
    accent: '#bcc6da', visualEffect: 'moon-fire', visualEffectLabel: '市桥 · 孤星',
  },
  {
    id: 'zhao-yi-lun-shi', title: '论诗五首·其二', author: '赵翼', dynasty: 'qing',
    lines: [
      '李杜诗篇万口传，至今已觉不新鲜。',
      '江山代有才人出，各领风骚数百年。',
    ],
    ...when(1790, '约1786—1795年', '乾隆后期 · 赵翼归里', 'range', '赵翼乾隆三十八年辞官后长期居家著述，《论诗》组诗收入晚年诗集，只能系于乾隆后期。'),
    ...at('changzhou-yanghu', '阳湖 · 赵翼故里', 119.95, 31.8),
    relation: 'associated', confidence: 'low',
    evidence: '赵翼是常州阳湖人，辞官后主讲安定书院并居乡著述，以故里作关联地。',
    sourceLabel: '《瓯北诗钞》', sourceUrl,
    accent: '#c7b27c', visualEffect: 'cloud-crane', visualEffectLabel: '李杜 · 风骚',
  },
  {
    id: 'lin-zexu-fu-shu', title: '赴戍登程口占示家人·其二', author: '林则徐', dynasty: 'qing',
    lines: [
      '力微任重久神疲，再竭衰庸定不支。苟利国家生死以，岂因祸福避趋之？',
      '谪居正是君恩厚，养拙刚于戍卒宜。戏与山妻谈故事，试吟断送老头皮。',
    ],
    ...when(1842, '1842年八月', '道光二十二年 · 西安登程', 'exact', '林则徐被革职遣戍伊犁，道光二十二年八月从西安启程，临行口占两首诗留别家人。'),
    ...at('xian-dengcheng', '西安 · 遣戍伊犁登程处', 108.95, 34.26),
    relation: 'composed_at', confidence: 'high',
    evidence: '诗题写明赴戍登程，林则徐因病在西安暂留后由此出发，家人留在西安。',
    sourceLabel: '《云左山房诗钞》卷六', sourceUrl,
    accent: '#b77b55', visualEffect: 'sun-river', visualEffectLabel: '登程 · 戍边',
  },
  {
    id: 'tan-sitong-yu-zhong', title: '狱中题壁', author: '谭嗣同', dynasty: 'qing',
    lines: [
      '望门投止思张俭，忍死须臾待杜根。',
      '我自横刀向天笑，去留肝胆两昆仑。',
    ],
    ...when(1898, '1898年九月', '光绪二十四年 · 戊戌政变', 'exact', '戊戌政变后谭嗣同拒绝出走，九月二十四日被捕，二十八日就义，诗题于刑部狱壁。'),
    ...at('beijing-xingbu-yu', '京师 · 刑部大狱', 116.39, 39.89),
    relation: 'composed_at', confidence: 'high',
    evidence: '诗题狱中题壁，刑部狱在宣武门内；数日后就义的菜市口也在附近。',
    sourceLabel: '梁启超《饮冰室诗话》', sourceUrl,
    accent: '#a8403a', visualEffect: 'petals-embers', visualEffectLabel: '横刀 · 昆仑',
  },
  {
    id: 'qiu-jin-man-jiang-hong', title: '满江红·小住京华', author: '秋瑾', dynasty: 'qing',
    lines: [
      '小住京华，早又是中秋佳节。为篱下黄花开遍，秋容如拭。四面歌残终破楚，八年风味徒思浙。苦将侬强派作蛾眉，殊未屑！',
      '身不得，男儿列；心却比，男儿烈！算平生肝胆，因人常热。俗子胸襟谁识我？英雄末路当磨折。莽红尘何处觅知音？青衫湿！',
    ],
    ...when(1903, '1903年中秋', '光绪二十九年 · 京师', 'exact', '秋瑾随夫王廷钧捐官居京，光绪二十九年中秋与丈夫冲突后作此词，次年东渡日本。'),
    ...at('beijing-qiujin', '京师 · 秋瑾南半截胡同寓所', 116.37, 39.89),
    relation: 'composed_at', confidence: 'high',
    evidence: '首句小住京华即说明在北京，秋瑾当时寓居宣南南半截胡同一带。',
    sourceLabel: '《秋瑾集》', sourceUrl,
    accent: '#c0584e', visualEffect: 'petals-embers', visualEffectLabel: '黄花 · 肝胆',
  },
]
